import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { Fragment, useEffect, useMemo, useState } from "react";
import API from "./api";
import {
  readPersistedCart,
  writePersistedCart,
  hydrateCartAfterAuth,
  persistLogoutCartCleanup,
  syncCartToServerBeforeLogout,
  clearServerAndLocalCart
} from "./cartStorage";
import { formatPageTitle, SITE_BRAND_TITLE } from "./siteMeta";
import CategoryExploreGrid from "./components/CategoryExploreGrid";
import HomeHeroPremium from "./components/HomeHeroPremium";
import ProductDescription from "./components/ProductDescription";
import AIProductDescriptionGenerator from "./components/AIProductDescriptionGenerator";

const orderLineExtras = (item) => {
  const bits = [];
  if (item.size) bits.push(`Size: ${item.size}`);
  if (item.color) bits.push(`Color: ${item.color}`);
  if (String(item.packLabel || "").trim()) bits.push(`Pack: ${String(item.packLabel).trim()}`);
  if (String(item.customText || "").trim()) bits.push(`Custom text: ${String(item.customText).trim()}`);
  if (String(item.customImage || "").trim()) bits.push(`Custom image: ${String(item.customImage).trim()}`);
  return bits;
};

const HORIZONTAL_BANNER_MAIN = "__BANNER__";
const HORIZONTAL_BANNER_SUB_PREFIX = "__BANNER_SUB__::";
const horizontalBannerSubKey = (subName) => `${HORIZONTAL_BANNER_SUB_PREFIX}${subName}`;
const parseHorizontalBannerTarget = (subCategory) => {
  if (subCategory === HORIZONTAL_BANNER_MAIN) return { type: "main" };
  if (String(subCategory || "").startsWith(HORIZONTAL_BANNER_SUB_PREFIX)) {
    return { type: "sub", subName: String(subCategory).slice(HORIZONTAL_BANNER_SUB_PREFIX.length) };
  }
  return null;
};
const isHorizontalBannerRecord = (subCategory) =>
  subCategory === HORIZONTAL_BANNER_MAIN || String(subCategory || "").startsWith(HORIZONTAL_BANNER_SUB_PREFIX);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const raw = localStorage.getItem("user");
  const token = localStorage.getItem("token");
  if (!raw || !token) return <Navigate to="/auth" replace />;
  const user = JSON.parse(raw);
  const hasAdminAccess = Boolean(user?.isAdmin) || user?.role === "super_admin" || user?.role === "sub_admin";
  if (adminOnly && !hasAdminAccess) return <Navigate to="/" replace />;
  return children;
};

const AuthPage = ({ initialMode = "login" }) => {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptOtpConsent, setAcceptOtpConsent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [login2faStep, setLogin2faStep] = useState(false);
  const [login2faPendingToken, setLogin2faPendingToken] = useState("");
  const [login2faOtp, setLogin2faOtp] = useState("");
  const [message, setMessage] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const navigate = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    try {
      setMessage("");
      if (!acceptTerms || !acceptOtpConsent) {
        setMessage("Please accept required checkboxes to continue.");
        return;
      }
      const { data } = await API.post("/auth/request-email-otp", {
        name,
        email,
        phone,
        password
      });
      setOtpRequested(true);
      setDevOtp(data.devOtp || "");
      setMessage("OTP sent to your email.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to request OTP.");
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/auth/verify-email-otp", { email, otp });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      await hydrateCartAfterAuth();
      navigate("/", { replace: true });
    } catch (error) {
      setMessage(error?.response?.data?.message || "OTP verification failed.");
    }
  };

  const loginWithEmailPassword = async (e) => {
    e.preventDefault();
    try {
      setMessage("");
      const { data } = await API.post("/auth/login", { email, password });
      if (data.requiresTwoFactor && data.pendingToken) {
        setLogin2faStep(true);
        setLogin2faPendingToken(data.pendingToken);
        setLogin2faOtp("");
        setDevOtp(data.devOtp || "");
        setMessage(data.message || "Enter the code sent to your email.");
        return;
      }
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      await hydrateCartAfterAuth();
      navigate("/", { replace: true });
    } catch (error) {
      setMessage(error?.response?.data?.message || "Login failed.");
    }
  };

  const verifyLogin2fa = async (e) => {
    e.preventDefault();
    try {
      setMessage("");
      const { data } = await API.post("/auth/verify-login-2fa", {
        pendingToken: login2faPendingToken,
        otp: login2faOtp
      });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setLogin2faStep(false);
      setLogin2faPendingToken("");
      setLogin2faOtp("");
      await hydrateCartAfterAuth();
      navigate("/", { replace: true });
    } catch (error) {
      setMessage(error?.response?.data?.message || "Verification failed.");
    }
  };

  const resendLogin2fa = async () => {
    try {
      setMessage("");
      const { data } = await API.post("/auth/resend-login-2fa", { pendingToken: login2faPendingToken });
      setDevOtp(data.devOtp || "");
      setMessage(data.message || "New code sent.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Could not resend code.");
    }
  };

  return (
    <div className="auth-wrapper">
      <form
        onSubmit={
          mode === "signup"
            ? otpRequested
              ? verifyOtp
              : requestOtp
            : login2faStep
              ? verifyLogin2fa
              : loginWithEmailPassword
        }
        className="card auth-card"
      >
        <h2>{mode === "signup" ? "New Customer" : "Login with Email"}</h2>
        <div className="mode-switch">
          <button
            type="button"
            className={mode === "login" ? "active" : ""}
            onClick={() => {
              setMode("login");
              setOtpRequested(false);
              setLogin2faStep(false);
              setLogin2faPendingToken("");
              setLogin2faOtp("");
              setMessage("");
            }}
          >
            Login
          </button>
          <button
            type="button"
            className={mode === "signup" ? "active" : ""}
            onClick={() => {
              setMode("signup");
              setOtpRequested(false);
              setLogin2faStep(false);
              setLogin2faPendingToken("");
              setLogin2faOtp("");
              setMessage("");
            }}
          >
            Signup
          </button>
        </div>

        {mode === "signup" ? (
          <>
            <label className="field-label">Full Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name" required />
            <label className="field-label">Phone No.</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone No." required maxLength={15} />
            <label className="field-label">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required type="email" />
            <label className="field-label">Password</label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter Your Password"
              type="password"
              required
            />
          </>
        ) : login2faStep ? (
          <>
            <p className="auth-2fa-hint">Enter the 6-digit code we sent to your email to finish signing in.</p>
            <label className="field-label">Verification code</label>
            <input
              value={login2faOtp}
              onChange={(e) => setLogin2faOtp(e.target.value)}
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              required
            />
            <div className="auth-2fa-actions">
              <button type="button" className="btn-secondary" onClick={resendLogin2fa}>
                Resend code
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setLogin2faStep(false);
                  setLogin2faPendingToken("");
                  setLogin2faOtp("");
                  setMessage("");
                }}
              >
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="field-label">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required type="email" />
            <label className="field-label">Password</label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter Your Password"
              type="password"
              required
            />
          </>
        )}

        {mode === "signup" && otpRequested && (
          <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter 6-digit OTP" required />
        )}
        {mode === "signup" && !otpRequested && (
          <>
            <label className="checkbox-row">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
              <span>I accept the Terms & Conditions and have read the Privacy Policy.</span>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked={acceptOtpConsent} onChange={(e) => setAcceptOtpConsent(e.target.checked)} />
              <span>
                I consent to receive email OTP messages for verification and account updates.
              </span>
            </label>
          </>
        )}
        <button type="submit">
          {mode === "signup"
            ? otpRequested
              ? "Verify Email OTP"
              : "CREATE AN ACCOUNT"
            : login2faStep
              ? "Verify & sign in"
              : "Login"}
        </button>
        {message && <small>{message}</small>}
        {devOtp && <small>Dev OTP: {devOtp}</small>}
      </form>
    </div>
  );
};

const Home = ({
  selectedCategory,
  selectedSubCategory,
  searchTerm,
  categoryMenus,
  onSelectCategory,
  onSelectSubCategory,
  onOpenCategory
}) => {
  const [products, setProducts] = useState([]);
  const [categoryMedia, setCategoryMedia] = useState([]);
  const [banners, setBanners] = useState([]);
  const [bannerAnnouncement, setBannerAnnouncement] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [sortBy, setSortBy] = useState("relevance");
  const [isSubcategoryPanelOpen, setIsSubcategoryPanelOpen] = useState(true);
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const token = localStorage.getItem("token");
  const [cart, setCart] = useState(() => readPersistedCart());
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("wishlist") || "[]"));
  const [cartSuccessMessage, setCartSuccessMessage] = useState("");
  const [reviewSummary, setReviewSummary] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setLoadingProducts(true);
    Promise.all([API.get("/products"), API.get("/category-media"), API.get("/banners"), API.get("/shop-settings")])
      .then(([productsRes, mediaRes, bannersRes, settingsRes]) => {
        setProducts(productsRes.data || []);
        setCategoryMedia(mediaRes.data || []);
        setBanners(bannersRes.data || []);
        setBannerAnnouncement(String(settingsRes.data?.bannerAnnouncement || "").trim());
      })
      .catch(() => {
        setProducts([]);
        setCategoryMedia([]);
        setBanners([]);
        setBannerAnnouncement("");
      })
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    API.get("/reviews/summary")
      .then((res) => setReviewSummary(res.data?.summary || {}))
      .catch(() => setReviewSummary({}));
  }, []);

  useEffect(() => writePersistedCart(cart), [cart]);
  useEffect(() => localStorage.setItem("wishlist", JSON.stringify(wishlist)), [wishlist]);

  const addToCart = (item) => {
    if (!token || !user) {
      navigate("/auth");
      return;
    }
    setCart((prev) => {
      const hit = prev.find((p) => p.productId === item._id);
      if (hit) return prev.map((p) => (p.productId === item._id ? { ...p, quantity: p.quantity + 1 } : p));
      return [...prev, { productId: item._id, name: item.name, price: item.price, quantity: 1, image: item.image || "" }];
    });
    setCartSuccessMessage("Product Added Successfully!");
  };

  const toggleWishlist = (item) => {
    if (!token || !user) {
      navigate("/auth");
      return;
    }
    setWishlist((prev) => {
      const exists = prev.some((p) => p._id === item._id);
      if (exists) return prev.filter((p) => p._id !== item._id);
      return [...prev, item];
    });
  };

  const filteredProducts = products.filter((p) => {
    const matchesCategory = selectedCategory ? p.category === selectedCategory : true;
    const matchesSubCategory = selectedSubCategory ? p.subCategory === selectedSubCategory : true;
    const query = searchTerm.trim().toLowerCase();
    const matchesSearch = query
      ? `${p.name} ${p.description || ""} ${p.category || ""} ${p.subCategory || ""}`.toLowerCase().includes(query)
      : true;
    return matchesCategory && matchesSubCategory && matchesSearch;
  });

  const getRatingInfo = (product) => {
    const row = reviewSummary[String(product._id || "")];
    if (!row || !row.count) return { rating: null, reviews: 0 };
    return { rating: Number(row.avgRating).toFixed(1), reviews: row.count };
  };

  const getDiscountInfo = (product) => {
    const oldPrice = Number((Number(product.price || 0) * 1.35).toFixed(0));
    const discountPercent = Number(((1 - Number(product.price || 0) / oldPrice) * 100).toFixed(0));
    return { oldPrice, discountPercent };
  };

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    if (sortBy === "price-asc") list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    if (sortBy === "price-desc") list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    if (sortBy === "rating") {
      list.sort((a, b) => {
        const ra = reviewSummary[String(a._id)] || {};
        const rb = reviewSummary[String(b._id)] || {};
        const ca = ra.count || 0;
        const cb = rb.count || 0;
        if (!ca && !cb) return 0;
        if (!ca) return 1;
        if (!cb) return -1;
        const diff = Number(rb.avgRating || 0) - Number(ra.avgRating || 0);
        if (diff !== 0) return diff;
        return cb - ca;
      });
    }
    return list;
  }, [filteredProducts, sortBy, reviewSummary]);

  const recommendedProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => {
        const ra = reviewSummary[String(a._id)] || {};
        const rb = reviewSummary[String(b._id)] || {};
        const ca = ra.count || 0;
        const cb = rb.count || 0;
        if (!ca && !cb) return 0;
        if (!ca) return 1;
        if (!cb) return -1;
        const diff = Number(rb.avgRating || 0) - Number(ra.avgRating || 0);
        if (diff !== 0) return diff;
        return cb - ca;
      })
      .slice(0, 4);
  }, [products, reviewSummary]);

  const getCategoryPreview = (categoryTitle) =>
    categoryMedia.find((m) => m.category === categoryTitle && m.subCategory === "")?.image ||
    products.find((p) => p.category === categoryTitle)?.image ||
    "";
  const getSubCategoryPreview = (categoryTitle, subCategoryTitle) =>
    categoryMedia.find((m) => m.category === categoryTitle && m.subCategory === subCategoryTitle)?.image ||
    products.find((p) => p.category === categoryTitle && p.subCategory === subCategoryTitle)?.image ||
    "";
  const activeMenu = categoryMenus.find((menu) => menu.title === selectedCategory);
  const categoryMeta = {
    "Packing material": {
      icon: "📦",
      description: "Boxes, wraps & protective supplies",
      trending: true
    },
    "Grocery items": { icon: "🛒", description: "Pantry staples & everyday essentials" },
    "Custom gift items": { icon: "🎁", description: "Personalized picks for every occasion" },
    "Beauty items": { icon: "✨", description: "Skincare, care & daily beauty" },
    "Wooden items": { icon: "🪵", description: "Hand-finished wood décor & utility" }
  };

  const announcementText = bannerAnnouncement.trim();
  const showAnnouncementBar = Boolean(announcementText);

  return (
    <>
      <HomeHeroPremium banners={banners} offerLines={showAnnouncementBar ? [announcementText] : []} />
      {(selectedCategory || selectedSubCategory || searchTerm) && (
        <div className="active-filter-bar">
          <span>
            Showing: {selectedCategory || "All Categories"}
            {selectedSubCategory ? ` / ${selectedSubCategory}` : ""}
            {searchTerm ? ` / "${searchTerm}"` : ""}
          </span>
        </div>
      )}
      <section id="explore-categories" className="category-showcase explore-categories-premium">
        <div className="explore-categories-premium__head">
          <span className="explore-categories-premium__badge">Featured Categories</span>
          <h2 className="explore-categories-premium__title">Explore Categories</h2>
          <p className="explore-categories-premium__subtitle">
            From corrugated packaging and mailers to grocery, beauty, gifts, and woodcraft — tap a category to shop the collection.
          </p>
        </div>
        <CategoryExploreGrid
          categoryMenus={categoryMenus}
          categoryMeta={categoryMeta}
          products={products}
          selectedCategory={selectedCategory}
          loadingProducts={loadingProducts}
          getCategoryPreview={getCategoryPreview}
          onSelectCategory={onSelectCategory}
          onSelectSubCategory={onSelectSubCategory}
          onOpenCategory={onOpenCategory}
        />
      </section>
      {activeMenu && (
        <section className="subcategory-filter-panel">
          <div className="subcategory-panel-head">
            <button type="button" className="subcategory-collapse-btn" onClick={() => setIsSubcategoryPanelOpen((v) => !v)}>
              <span>{categoryMeta[activeMenu.title]?.icon || "📂"}</span>
              <strong>{activeMenu.title} Filters</strong>
              <span>{isSubcategoryPanelOpen ? "▲" : "▼"}</span>
            </button>
          </div>
          {isSubcategoryPanelOpen && (
            <div className="subcategory-chip-section">
              {activeMenu.items.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`subcategory-chip ${selectedSubCategory === item ? "active" : ""}`}
                  onClick={() => {
                    onSelectSubCategory(item);
                    onOpenCategory(activeMenu.title, item);
                  }}
                >
                  <div className="subcategory-chip-image">
                    {getSubCategoryPreview(activeMenu.title, item) ? (
                      <img src={getSubCategoryPreview(activeMenu.title, item)} alt={item} />
                    ) : (
                      <span>{item.slice(0, 2).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="subcategory-chip-text">
                    <span>{item}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}
      <section className="section-head-row">
        <h3>Featured Products</h3>
        <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="relevance">Sort: Relevance</option>
          <option value="rating">Sort: Rating</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
        </select>
      </section>
      <section id="products" className="grid">
        {loadingProducts &&
          Array.from({ length: 8 }).map((_, idx) => (
            <div key={`skeleton-${idx}`} className="card product-card premium-card skeleton-card">
              <div className="product-image skeleton-block" />
              <div className="product-content">
                <div className="skeleton-line" />
                <div className="skeleton-line short" />
                <div className="skeleton-line short" />
              </div>
            </div>
          ))}
        {!loadingProducts &&
          sortedProducts.map((p) => {
            const { rating, reviews } = getRatingInfo(p);
            const { oldPrice, discountPercent } = getDiscountInfo(p);
            return (
              <div key={p._id} className="card product-card premium-card" onClick={() => navigate(`/product/${p._id}`)} role="button">
                <button
                  type="button"
                  className={`wishlist-btn ${wishlist.some((w) => w._id === p._id) ? "active" : ""}`}
                  aria-label="Add to wishlist"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleWishlist(p);
                  }}
                >
                  {wishlist.some((w) => w._id === p._id) ? "❤" : "♡"}
                </button>
                <div className="product-image">
                  {p.image ? (
                    <img src={p.image} alt={p.name} />
                  ) : (
                    <div className="image-fallback">
                      <span>{p.name}</span>
                    </div>
                  )}
                </div>
                <div className="product-content">
                  <small className="category-tag">{p.category}</small>
                  <h3 className="product-title">{p.name}</h3>
                  <p className="rating-row">
                    {reviews > 0 ? (
                      <>
                        <span className="rating-stars" aria-hidden>
                          ★
                        </span>{" "}
                        <span className="rating-value">{rating}</span>
                        <span className="rating-count">
                          {" "}
                          ({reviews} {reviews === 1 ? "review" : "reviews"})
                        </span>
                      </>
                    ) : (
                      <span className="rating-empty">No reviews yet</span>
                    )}
                  </p>
                  <div className="price-row">
                    <p className="product-price">₹ {p.price}</p>
                    <p className="product-old-price">₹ {oldPrice}</p>
                    <p className="discount-pill">{discountPercent}% OFF</p>
                  </div>
                  <p className="delivery-text">🚚 Delivery by Tomorrow</p>
                  {Number(p.stock || 0) > 0 && Number(p.stock || 0) <= 3 && <p className="stock-warning">Only {p.stock} left</p>}
                  {Number(p.stock || 0) <= 0 && <p className="stock-warning">Out of stock</p>}
                  <div className="product-card-footer">
                    <small className="product-mini-note">{p.subCategory || "Product"}</small>
                    <div className="product-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(p);
                        }}
                        className="add-btn"
                        disabled={Number(p.stock || 0) <= 0}
                      >
                        Add to Cart
                      </button>
                      <button
                        type="button"
                        className="buy-btn"
                        disabled={Number(p.stock || 0) <= 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(p);
                          navigate("/checkout");
                        }}
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        {!loadingProducts && !sortedProducts.length && (
          <div className="empty-state card">
            <h4>No products found</h4>
            <p>Try changing filters, category or search terms.</p>
            <button
              type="button"
              onClick={() => {
                onSelectCategory("");
                onSelectSubCategory("");
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </section>
      {!loadingProducts && recommendedProducts.length > 0 && (
        <section className="recommended-section">
          <div className="section-head-row">
            <h3>Recommended for you</h3>
          </div>
          <div className="grid recommended-grid">
            {recommendedProducts.map((p) => (
              <div
                key={`rec-${p._id}`}
                className="card product-card premium-card"
                onClick={() => navigate(`/product/${p._id}`)}
                role="button"
              >
                <div className="product-image">{p.image ? <img src={p.image} alt={p.name} /> : <div className="image-fallback">{p.name}</div>}</div>
                <div className="product-content">
                  <small className="category-tag">{p.category}</small>
                  <h3 className="product-title">{p.name}</h3>
                  <div className="price-row">
                    <p className="product-price">₹ {p.price}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart(p);
                    }}
                    className="add-btn"
                    disabled={Number(p.stock || 0) <= 0}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {cartSuccessMessage && (
        <div className="cart-success-overlay" role="dialog" aria-modal="true">
          <div className="cart-success-modal">
            <div className="cart-success-icon">✓</div>
            <p>{cartSuccessMessage}</p>
            <button type="button" className="cart-success-btn" onClick={() => setCartSuccessMessage("")}>
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const CategoryWindow = ({ categoryMenus }) => {
  const { categoryName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCategory = decodeURIComponent(categoryName || "");
  const selectedSubCategory = new URLSearchParams(location.search).get("sub") || "";
  const [products, setProducts] = useState([]);
  const [categoryMedia, setCategoryMedia] = useState([]);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    const menuLocal = categoryMenus.find((item) => item.title === selectedCategory);
    const flatCategory = !menuLocal?.items?.length;
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (selectedSubCategory && !flatCategory) params.subCategory = selectedSubCategory;
    API.get("/products", { params }).then((res) => setProducts(res.data));
    API.get("/category-media").then((res) => setCategoryMedia(res.data)).catch(() => setCategoryMedia([]));
  }, [selectedCategory, selectedSubCategory, categoryMenus]);

  const menu = categoryMenus.find((item) => item.title === selectedCategory);
  const flatCategory = !menu?.items?.length;
  const subBannerKey =
    selectedSubCategory && !flatCategory ? horizontalBannerSubKey(selectedSubCategory) : null;
  const bannerImage =
    (subBannerKey
      ? categoryMedia.find((m) => m.category === selectedCategory && m.subCategory === subBannerKey)?.image
      : null) ||
    categoryMedia.find((m) => m.category === selectedCategory && m.subCategory === HORIZONTAL_BANNER_MAIN)?.image ||
    categoryMedia.find((m) => m.category === selectedCategory && m.subCategory === "")?.image ||
    products[0]?.image ||
    "";
  const subCategoryImage = (item) =>
    categoryMedia.find((m) => m.category === selectedCategory && m.subCategory === item)?.image ||
    products.find((p) => p.subCategory === item)?.image ||
    "";

  return (
    <div className={`category-window${flatCategory ? " category-window-flat" : ""}`}>
      <div className="card category-banner">
        {bannerImage ? <img src={bannerImage} alt={selectedCategory} /> : <h2>{selectedCategory}</h2>}
      </div>
      {!flatCategory && (
        <div className="category-layout">
          <aside className="card category-side">
            <button type="button" className="subcategory-collapse-btn" onClick={() => setShowFilters((v) => !v)}>
              <span>🧭</span>
              <strong>Sub Categories</strong>
              <span>{showFilters ? "▲" : "▼"}</span>
            </button>
            {showFilters &&
              menu?.items.map((item) => (
                <Link
                  key={item}
                  to={`/category/${encodeURIComponent(selectedCategory)}?sub=${encodeURIComponent(item)}`}
                  className={`side-link gradient-pill ${selectedSubCategory === item ? "active" : ""}`}
                >
                  {item}
                </Link>
              ))}
          </aside>
          <div className="category-products">
            {(selectedSubCategory ? [selectedSubCategory] : menu?.items || []).slice(0, 8).map((item) => (
              <Link
                key={item}
                to={`/category/${encodeURIComponent(selectedCategory)}?sub=${encodeURIComponent(item)}`}
                className="card subcategory-tile"
              >
                <div className="subcategory-chip-image large">
                  {subCategoryImage(item) ? <img src={subCategoryImage(item)} alt={item} /> : <span>{item.slice(0, 2)}</span>}
                </div>
                <div className="subcategory-tile-text">
                  <span>{item}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="grid">
        {products.map((p) => (
          <div key={p._id} className="card product-card premium-card" onClick={() => navigate(`/product/${p._id}`)} role="button">
            <div className="product-image">{p.image ? <img src={p.image} alt={p.name} /> : <div className="image-fallback">{p.name}</div>}</div>
            <div className="product-content">
              <h3 className="product-title">{p.name}</h3>
              <p className="product-price">₹ {p.price}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [activeImage, setActiveImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("Standard");
  const [selectedColor, setSelectedColor] = useState("Natural");
  const [pincode, setPincode] = useState("");
  const [deliveryInfo, setDeliveryInfo] = useState({ text: "Check delivery by pincode", shippingCost: 0, codAvailable: true });
  const [activeTab, setActiveTab] = useState("description");
  const [loading, setLoading] = useState(true);
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(user?.email || "");
  const [myRating, setMyRating] = useState(5);
  const [myComment, setMyComment] = useState("");
  const [reviewMessage, setReviewMessage] = useState("");
  const [customText, setCustomText] = useState("");
  const [customImage, setCustomImage] = useState("");
  const [fontScale, setFontScale] = useState(1);
  const [darkMode, setDarkMode] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [selectedPackIdx, setSelectedPackIdx] = useState(0);
  const sizes = ["Small", "Medium", "Standard", "Large"];
  const colors = ["Natural", "Black", "Blue"];
  const sizeStockMap = { Small: 0, Medium: 3, Standard: 6, Large: 2 };
  const colorPriceDelta = { Natural: 0, Black: 20, Blue: 30 };
  const galleryImages = useMemo(() => {
    if (!product) return [];
    const urls = [product.image, ...(Array.isArray(product.images) ? product.images : [])].filter(Boolean);
    return [...new Set(urls)];
  }, [product]);
  const packList = product?.packOptions?.length ? product.packOptions : null;
  const isCustomGift = String(product?.category || "").toLowerCase().includes("custom gift");

  useEffect(() => {
    setSelectedPackIdx(0);
  }, [productId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([API.get(`/products/${productId}`), API.get("/products"), API.get(`/reviews/product/${productId}`)])
      .then(([singleRes, productsRes, reviewsRes]) => {
        const current = singleRes.data;
        setProduct(current);
        setActiveImage(current.image || "");
        const related = (productsRes.data || []).filter((item) => item._id !== current._id && item.category === current.category).slice(0, 8);
        setRelatedProducts(related);
        setProductReviews(reviewsRes.data?.reviews || []);
        const allProducts = productsRes.data || [];
        const productMap = new Map(allProducts.map((item) => [item._id, item]));

        // Migrate old object-based storage into ID-based storage for consistency.
        const legacyViewed = JSON.parse(localStorage.getItem("recentlyViewedProducts") || "[]");
        const legacyIds = Array.isArray(legacyViewed) ? legacyViewed.map((item) => item?._id).filter(Boolean) : [];
        const storedIds = JSON.parse(localStorage.getItem("recentlyViewedProductIds") || "[]");
        const seedIds = Array.isArray(storedIds) && storedIds.length ? storedIds : legacyIds;

        const nextViewedIds = [current._id, ...seedIds.filter((id) => id && id !== current._id)].slice(0, 12);
        localStorage.setItem("recentlyViewedProductIds", JSON.stringify(nextViewedIds));

        const nextViewedProducts = nextViewedIds
          .filter((id) => id !== current._id)
          .map((id) => productMap.get(id))
          .filter(Boolean)
          .slice(0, 8);

        setRecentlyViewed(nextViewedProducts);
      })
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    if (!product?.name) return;
    if (String(product._id) !== String(productId)) return;
    document.title = formatPageTitle(product.name);
  }, [product, productId]);

  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 460);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontScale * 100}%`;
    document.body.classList.toggle("dark-mode", darkMode);
  }, [fontScale, darkMode]);

  const ratingSeed = useMemo(() => String(product?._id || "").split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0), [product?._id]);
  const hasReviews = productReviews.length > 0;
  const ratingValue = useMemo(() => {
    if (!productReviews.length) return null;
    const avg = productReviews.reduce((sum, item) => sum + Number(item.rating || 0), 0) / productReviews.length;
    return avg.toFixed(1);
  }, [productReviews]);
  const reviewCount = productReviews.length;
  const baseSelling = useMemo(() => {
    if (!product) return 0;
    if (packList && packList[selectedPackIdx]) return Number(packList[selectedPackIdx].price) || 0;
    return Number(product.price) || 0;
  }, [product, packList, selectedPackIdx]);
  const colorDelta = Number(colorPriceDelta[selectedColor] || 0);
  const dynamicPrice = baseSelling + colorDelta;
  const listPriceRaw = useMemo(() => {
    if (!product) return 0;
    const cmp = product.compareAtPrice;
    if (cmp != null && cmp !== "" && !Number.isNaN(Number(cmp))) return Number(cmp);
    return Number((baseSelling * 1.35).toFixed(0));
  }, [product, baseSelling]);
  const listPrice = listPriceRaw + colorDelta;
  const discountPercent = listPrice > 0 ? Number(((1 - dynamicPrice / listPrice) * 100).toFixed(0)) : 0;
  const baseStock = Number(product?.stock || 0);
  const sizeStock = sizeStockMap[selectedSize] ?? baseStock;
  const stock = Math.max(0, Math.min(baseStock, sizeStock));
  const insightBadge = useMemo(() => {
    if (!productReviews.length) return "New arrival";
    const avg = productReviews.reduce((s, r) => s + Number(r.rating || 0), 0) / productReviews.length;
    const n = productReviews.length;
    if (avg >= 4.5 && n >= 5) return "Top rated";
    if (n >= 10) return "Popular";
    if (avg >= 4) return "Loved by buyers";
    return "In stock";
  }, [productReviews]);

  const addToCart = () => {
    if (!token || !user) {
      navigate("/auth");
      return;
    }
    const packLabel = packList && packList[selectedPackIdx] ? packList[selectedPackIdx].label : "";
    const currentCart = readPersistedCart();
    const hit = currentCart.find(
      (item) =>
        item.productId === product._id &&
        item.size === selectedSize &&
        item.color === selectedColor &&
        (item.packLabel || "") === packLabel
    );
    const nextCart = hit
      ? currentCart.map((item) =>
          item.productId === product._id &&
          item.size === selectedSize &&
          item.color === selectedColor &&
          (item.packLabel || "") === packLabel
            ? { ...item, quantity: Math.min(item.quantity + quantity, Math.max(stock, 1)) }
            : item
        )
      : [
          ...currentCart,
          {
            productId: product._id,
            name: product.name,
            price: dynamicPrice,
            image: activeImage || product.image || "",
            quantity,
            size: selectedSize,
            color: selectedColor,
            packLabel,
            customText,
            customImage
          }
        ];
    writePersistedCart(nextCart);
    API.put("/auth/cart-sync", { cart: nextCart }).catch(() => {});
    setCartPreviewOpen(true);
  };

  const predictDelivery = (pin = pincode) => {
    if (!pin || pin.length < 6) {
      setDeliveryInfo({ text: "Please enter a valid pincode", shippingCost: 0, codAvailable: false });
      return;
    }
    const estimate = new Date();
    const days = (Number(String(pin).slice(-1)) % 3) + 2;
    estimate.setDate(estimate.getDate() + days);
    const shippingCost = Number(String(pin).slice(-1)) % 2 === 0 ? 40 : 70;
    const codAvailable = Number(String(pin).slice(-1)) % 5 !== 0;
    setDeliveryInfo({
      text: `Delivery by ${estimate.toLocaleDateString()}`,
      shippingCost,
      codAvailable
    });
  };

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      () => {
        const pseudoPin = `15211${(ratingSeed % 10).toString()}`;
        setPincode(pseudoPin);
        predictDelivery(pseudoPin);
      },
      () => {}
    );
  };

  const notifyMe = async () => {
    if (!notifyEmail || !product?._id) return;
    try {
      const { data } = await API.post("/product-alerts", { productId: product._id, email: notifyEmail });
      alert(data.message);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to subscribe alert");
    }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Check this product: ${product.name} - ${window.location.href}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareInstagram = () => {
    navigator.clipboard?.writeText(window.location.href);
    alert("Product link copied! Paste it on Instagram.");
  };

  const reviewBreakdown = useMemo(() => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    productReviews.forEach((item) => {
      const r = Math.max(1, Math.min(5, Number(item.rating || 0)));
      breakdown[Math.round(r)] += 1;
    });
    return breakdown;
  }, [productReviews]);

  useEffect(() => {
    if (!user?._id) return;
    const mine = productReviews.find((r) => r.userId?._id === user._id);
    if (mine) {
      setMyRating(Number(mine.rating || 5));
      setMyComment(mine.comment || "");
    } else {
      setMyRating(5);
      setMyComment("");
    }
  }, [productReviews, user?._id]);

  const submitReview = async () => {
    if (!token) {
      navigate("/auth");
      return;
    }
    try {
      const { data } = await API.post("/reviews", {
        productId: product._id,
        rating: Number(myRating),
        comment: myComment.trim()
      });
      const next = (productReviews || []).filter((r) => r._id !== data.review?._id && r.userId?._id !== user?._id);
      setProductReviews([data.review, ...next]);
      setReviewMessage("Review saved.");
      window.setTimeout(() => setReviewMessage(""), 2500);
    } catch (error) {
      setReviewMessage(error?.response?.data?.message || "Failed to save review");
      window.setTimeout(() => setReviewMessage(""), 3000);
    }
  };

  const starFillCount = hasReviews ? Math.min(5, Math.max(0, Math.round(Number(ratingValue)))) : 0;

  if (loading) {
    return (
      <div className="pdp-page pdp-page--premium pdp-page--premium-loading">
        <div className="pdp-page-premium-bg" aria-hidden>
          <div className="pdp-page-premium-orb pdp-page-premium-orb--1" />
          <div className="pdp-page-premium-orb pdp-page-premium-orb--2" />
        </div>
        <div className="pdp-grid pdp-grid--premium">
          <div className="pdp-gallery pdp-gallery--premium skeleton-block pdp-skeleton-premium" />
          <div className="pdp-details pdp-details--premium skeleton-block pdp-skeleton-premium" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pdp-page pdp-page--premium">
        <div className="pdp-not-found-glass card">Product not found.</div>
      </div>
    );
  }

  return (
    <div className="pdp-page pdp-page--premium">
      <div className="pdp-page-premium-bg" aria-hidden>
        <div className="pdp-page-premium-orb pdp-page-premium-orb--1" />
        <div className="pdp-page-premium-orb pdp-page-premium-orb--2" />
        <div className="pdp-page-premium-gridlines" />
        <div className="pdp-page-premium-dots" />
      </div>

      <div className="pdp-accessibility-bar pdp-accessibility-bar--premium">
        <button type="button" onClick={() => setFontScale((s) => Math.max(0.9, Number((s - 0.1).toFixed(1))))}>
          A-
        </button>
        <button type="button" onClick={() => setFontScale((s) => Math.min(1.3, Number((s + 0.1).toFixed(1))))}>
          A+
        </button>
        <button type="button" onClick={() => setDarkMode((d) => !d)}>
          {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      <div className="pdp-grid pdp-grid--premium">
        <section className="card pdp-gallery pdp-gallery--premium">
          <span className="pdp-trending-badge">{insightBadge}</span>
          <div className="pdp-main-image pdp-main-image--premium zoom-lens-wrap">
            <div className="pdp-main-image-glow" aria-hidden />
            {activeImage ? <img src={activeImage} alt={product.name} /> : <div className="image-fallback pdp-image-fallback">{product.name}</div>}
          </div>
          <div className="pdp-thumbs pdp-thumbs--premium">
            {galleryImages.map((img, idx) => (
              <button key={`${img}-${idx}`} type="button" className={`pdp-thumb pdp-thumb--premium ${activeImage === img ? "is-active" : ""}`} onClick={() => setActiveImage(img)}>
                <img src={img} alt={`${product.name} view ${idx + 1}`} />
              </button>
            ))}
          </div>
        </section>

        <section className="card pdp-details pdp-details--premium">
          <div className="pdp-highlight-tags">
            <span className="pdp-tag">Best for E-commerce</span>
            <span className="pdp-tag">High Durability</span>
          </div>

          <h1 className="pdp-title pdp-title--premium">{product.name}</h1>
          <p className="pdp-brand pdp-brand--premium">{product.category}</p>

          <div
            className={`pdp-rating-premium${!hasReviews ? " pdp-rating-premium--empty" : ""}`}
            aria-label={hasReviews ? `Rating ${ratingValue} out of 5` : "No customer reviews yet"}
          >
            {hasReviews ? (
              <>
                <span className="pdp-stars" aria-hidden>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className={i <= starFillCount ? "pdp-star pdp-star--on" : "pdp-star"}>
                      ★
                    </span>
                  ))}
                </span>
                <span className="pdp-rating-value">{ratingValue}</span>
                <span className="pdp-rating-count">
                  ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
                </span>
              </>
            ) : (
              <span className="pdp-rating-empty">No reviews yet — open the Reviews tab to share your experience</span>
            )}
          </div>

          <div className="price-row pdp-price-row--premium">
            <p className="product-price pdp-price--premium">
              ₹{" "}
              {Number(dynamicPrice).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {listPrice > dynamicPrice && (
              <>
                <p className="product-old-price pdp-old-price--premium">
                  ₹{" "}
                  {Number(listPrice).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="discount-pill pdp-discount-pill--premium">{discountPercent}% OFF</p>
              </>
            )}
          </div>

          {packList && packList.length > 0 && (
            <div className="pdp-pack-section">
              <span className="pdp-pack-label">Choose pack size</span>
              <div className="pdp-pack-grid">
                {packList.map((p, idx) => (
                  <button
                    key={`${p.label}-${idx}`}
                    type="button"
                    className={`pdp-pack-card ${selectedPackIdx === idx ? "is-active" : ""}`}
                    onClick={() => setSelectedPackIdx(idx)}
                  >
                    <span className="pdp-pack-card__name">{p.label}</span>
                    <span className="pdp-pack-card__price">
                      ₹{Number(p.price).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pdp-stock-ship-row">
            <span className={`pdp-stock-pill ${stock > 3 ? "pdp-stock-pill--ok" : stock > 0 ? "pdp-stock-pill--low" : "pdp-stock-pill--out"}`}>
              <span className="pdp-stock-pill__icon" aria-hidden>
                📦
              </span>
              {stock > 3 ? "In Stock" : stock > 0 ? `Only ${stock} left` : "Out of stock"}
            </span>
            <span className="pdp-ship-hint">
              <span aria-hidden>🚚</span> Pan-India delivery
            </span>
          </div>

          {stock <= 0 && (
            <div className="notify-box pdp-notify-premium">
              <input value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="Email for stock alert" type="email" />
              <button type="button" className="pdp-btn-check-delivery" onClick={notifyMe}>
                Notify me
              </button>
            </div>
          )}

          <div className="pdp-delivery-block">
            <span className="pdp-delivery-label">Check delivery</span>
            <div className="pdp-pincode-row">
              <label className="pdp-pincode-pill">
                <span className="pdp-pincode-pill__icon" aria-hidden>
                  📍
                </span>
                <input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Pincode" maxLength={6} inputMode="numeric" />
              </label>
              <button type="button" className="pdp-btn-check-delivery" onClick={() => predictDelivery()}>
                Check delivery
              </button>
              <button type="button" className="pdp-btn-location" onClick={detectLocation}>
                Use location
              </button>
            </div>
            <p className="pdp-delivery-meta">
              {deliveryInfo.text} · Shipping ₹{deliveryInfo.shippingCost} · COD {deliveryInfo.codAvailable ? "available" : "not available"}
            </p>
          </div>

          {isCustomGift && (
            <div className="customization-box">
              <strong className="customization-heading">Customization</strong>
              <input value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="Add custom text (name printing)" />
              <input value={customImage} onChange={(e) => setCustomImage(e.target.value)} placeholder="Image URL for printing / engraving" />
              <div className="custom-preview">
                <span className={customText.trim() ? "custom-preview-text" : "custom-preview-placeholder"}>
                  {customText.trim() || "Your text preview"}
                </span>
                {customImage?.trim() ? <img src={customImage.trim()} alt="Custom reference" /> : null}
              </div>
            </div>
          )}

          <div className="pdp-qty-row">
            <span className="pdp-qty-label">Quantity</span>
            <div className="qty-controls pdp-qty-controls">
              <button type="button" aria-label="Decrease quantity" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                −
              </button>
              <span className="pdp-qty-value">{quantity}</span>
              <button type="button" aria-label="Increase quantity" onClick={() => setQuantity((q) => Math.min(Math.max(stock, 1), q + 1))}>
                +
              </button>
            </div>
          </div>

          <div className="product-actions pdp-actions-premium">
            <button type="button" className="pdp-btn-cart" disabled={stock <= 0} onClick={addToCart}>
              Add to Cart
            </button>
            <button type="button" className="pdp-btn-buy" disabled={stock <= 0} onClick={() => { addToCart(); navigate("/checkout"); }}>
              Buy Now
            </button>
            <button type="button" className="pdp-icon-btn" aria-label="Wishlist" onClick={() => navigate("/wishlist")}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
            <button type="button" className="pdp-icon-btn" aria-label="Share on WhatsApp" onClick={shareWhatsApp}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </button>
            <button type="button" className="pdp-icon-btn" aria-label="Copy link for Instagram" onClick={shareInstagram}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </button>
          </div>

          <div className="pdp-trust-badges">
            <span className="pdp-trust-badge">🔒 Secure payment</span>
            <span className="pdp-trust-badge">⚡ Fast delivery</span>
          </div>
        </section>
      </div>

      <section className="card pdp-tabs pdp-tabs--premium">
        <div className="pdp-tab-row">
          {["description", "specifications", "reviews"].map((tab) => (
            <button key={tab} type="button" className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>
              {tab[0].toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        {activeTab === "description" && <ProductDescription text={product.description} />}
        {activeTab === "specifications" && (
          <table className="spec-table">
            <tbody>
              <tr><td>Category</td><td>{product.category}</td></tr>
              <tr><td>Sub Category</td><td>{product.subCategory}</td></tr>
              <tr><td>Stock</td><td>{product.stock}</td></tr>
              <tr><td>Variant Size</td><td>{selectedSize}</td></tr>
              <tr><td>Variant Color</td><td>{selectedColor}</td></tr>
              {packList && packList[selectedPackIdx] && (
                <tr><td>Pack</td><td>{packList[selectedPackIdx].label}</td></tr>
              )}
            </tbody>
          </table>
        )}
        {activeTab === "reviews" && (
          <div>
            <div className="review-submit-box">
              <strong>Write a Review</strong>
              <div className="variant-row">
                <span>Rating:</span>
                <select value={myRating} onChange={(e) => setMyRating(Number(e.target.value))}>
                  {[5, 4, 3, 2, 1].map((r) => (
                    <option key={r} value={r}>
                      {r} Star
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                value={myComment}
                onChange={(e) => setMyComment(e.target.value)}
                placeholder="Share your experience with this product..."
                rows={3}
              />
              <button type="button" className="add-btn" onClick={submitReview}>
                Submit Review
              </button>
              {reviewMessage && <small>{reviewMessage}</small>}
            </div>
            <div className="rating-breakdown">
              {[5, 4, 3, 2, 1].map((star) => (
                <div key={star} className="rating-break-row">
                  <span>{star}⭐</span>
                  <div className="rating-bar">
                    <div style={{ width: `${reviewCount ? (reviewBreakdown[star] / reviewCount) * 100 : 0}%` }} />
                  </div>
                  <span>{reviewBreakdown[star]}</span>
                </div>
              ))}
            </div>
            <div className="review-list">
              {productReviews.map((review) => {
                const uid = review.userId;
                const reviewerName =
                  (typeof uid === "object" && uid?.name && String(uid.name).trim()) ||
                  (typeof uid === "object" && uid?.email ? String(uid.email).split("@")[0] : "") ||
                  "Customer";
                return (
                <div key={review._id} className="review-item">
                  <div className="review-item-head">
                    <strong>{reviewerName}</strong>
                    <span className="review-item-meta">
                      {review.createdAt ? new Date(review.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" }) : ""}
                    </span>
                  </div>
                  <p className="review-item-rating">
                    <span className="review-stars-inline" aria-hidden>
                      {"★".repeat(Math.min(5, Math.max(0, Math.round(Number(review.rating || 0)))))}
                      {"☆".repeat(Math.max(0, 5 - Math.min(5, Math.max(0, Math.round(Number(review.rating || 0))))))}
                    </span>
                    <span className="review-rating-num">{Number(review.rating || 0).toFixed(0)}/5</span>
                  </p>
                  <p className="review-item-comment">{review.comment || "No written comment"}</p>
                  <div className="review-images">
                    {(review.images || []).map((img, idx) => (
                      <img key={`${review._id}-${idx}`} src={img} alt="Review upload" />
                    ))}
                  </div>
                </div>
                );
              })}
              {!productReviews.length && <p>No reviews yet.</p>}
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h3>Related Products</h3>
        <div className="grid recommended-grid">
          {relatedProducts.map((item) => (
            <div key={item._id} className="card product-card premium-card" onClick={() => navigate(`/product/${item._id}`)} role="button">
              <div className="product-image">{item.image ? <img src={item.image} alt={item.name} /> : <div className="image-fallback">{item.name}</div>}</div>
              <div className="product-content">
                <h3 className="product-title">{item.name}</h3>
                <p className="product-price">₹ {item.price}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h3>Recently Viewed Products</h3>
        <div className="grid recommended-grid">
          {recentlyViewed.map((item) => (
            <div key={`recent-${item._id}`} className="card product-card premium-card" onClick={() => navigate(`/product/${item._id}`)} role="button">
              <div className="product-image">{item.image ? <img src={item.image} alt={item.name} /> : <div className="image-fallback">{item.name}</div>}</div>
              <div className="product-content">
                <h3 className="product-title">{item.name}</h3>
                <p className="product-price">₹ {item.price}</p>
              </div>
            </div>
          ))}
          {!recentlyViewed.length && <p>No recently viewed products yet.</p>}
        </div>
      </section>

      {(showStickyBar || window.innerWidth < 900) && (
        <div className="global-sticky-buy global-sticky-buy--premium">
          <span>{product.name}</span>
          <strong>₹ {dynamicPrice}</strong>
          <button type="button" className="pdp-btn-cart pdp-btn-cart--compact" disabled={stock <= 0} onClick={addToCart}>
            Add to Cart
          </button>
          <button type="button" className="pdp-btn-buy pdp-btn-buy--compact" disabled={stock <= 0} onClick={() => navigate("/checkout")}>
            Buy Now
          </button>
        </div>
      )}

      <div className="mobile-sticky-buy mobile-sticky-buy--premium">
        <button type="button" className="pdp-btn-cart" disabled={stock <= 0} onClick={addToCart}>
          Add to Cart
        </button>
        <button type="button" className="pdp-btn-buy" disabled={stock <= 0} onClick={() => navigate("/checkout")}>
          Buy Now
        </button>
      </div>

      {cartPreviewOpen && (
        <div className="cart-preview-popup">
          <div>
            <strong>Added to cart</strong>
            <p>{product.name}</p>
          </div>
          <div className="product-actions">
            <button type="button" className="btn-secondary" onClick={() => setCartPreviewOpen(false)}>Continue</button>
            <button type="button" className="add-btn" onClick={() => navigate("/checkout")}>Go to Cart</button>
          </div>
        </div>
      )}
    </div>
  );
};

const DEFAULT_PRICING_CONFIG = { taxPercent: 0, deliveryFee: 50 };

const getPricingConfig = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("adminPricingConfig") || "{}");
    const taxPercent = Number(raw.taxPercent);
    const deliveryFee = Number(raw.deliveryFee);
    return {
      taxPercent: Number.isFinite(taxPercent) && taxPercent >= 0 ? taxPercent : DEFAULT_PRICING_CONFIG.taxPercent,
      deliveryFee: Number.isFinite(deliveryFee) && deliveryFee >= 0 ? deliveryFee : DEFAULT_PRICING_CONFIG.deliveryFee
    };
  } catch (_error) {
    return DEFAULT_PRICING_CONFIG;
  }
};

/** Subtotal at or above this unlocks free standard shipping at checkout (UI + charged amount). */
const FREE_SHIPPING_THRESHOLD = 1999;

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => readPersistedCart());
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("wishlist") || "[]"));
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    fullName: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    latitude: "",
    longitude: "",
    accuracy: "",
    locationLabel: ""
  });
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Online");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [stockByProductId, setStockByProductId] = useState({});
  const [pricingConfig] = useState(() => getPricingConfig());
  const [checkoutInitLoading, setCheckoutInitLoading] = useState(true);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const subtotal = useMemo(() => cart.reduce((sum, i) => sum + i.price * i.quantity, 0), [cart]);
  const baseShippingFee = subtotal === 0 ? 0 : Number(pricingConfig.deliveryFee || 0);
  const freeShippingUnlocked = subtotal > 0 && subtotal >= FREE_SHIPPING_THRESHOLD;
  const shippingCharged = freeShippingUnlocked ? 0 : baseShippingFee;
  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discountType === "PERCENT") {
      const percentValue = (subtotal * Number(appliedCoupon.discountValue || 0)) / 100;
      if (Number(appliedCoupon.maxDiscountAmount || 0) > 0) {
        return Number(Math.min(percentValue, Number(appliedCoupon.maxDiscountAmount)).toFixed(2));
      }
      return Number(percentValue.toFixed(2));
    }
    return Number(Math.min(Number(appliedCoupon.discountValue || 0), subtotal).toFixed(2));
  }, [appliedCoupon, subtotal]);
  const taxableAmount = Math.max(subtotal - discountAmount, 0);
  const taxAmount = Number(((taxableAmount * Number(pricingConfig.taxPercent || 0)) / 100).toFixed(2));
  const total = Number((taxableAmount + taxAmount + shippingCharged).toFixed(2));
  const savingsHighlight = Number(
    (discountAmount + (freeShippingUnlocked && baseShippingFee > 0 ? baseShippingFee : 0)).toFixed(2)
  );
  const freeShipRemaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const freeShipProgress = Math.min(100, subtotal <= 0 ? 0 : (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  useEffect(() => {
    writePersistedCart(cart);
    API.put("/auth/cart-sync", { cart }).catch(() => {});
  }, [cart]);

  useEffect(() => localStorage.setItem("wishlist", JSON.stringify(wishlist)), [wishlist]);

  useEffect(() => {
    setCheckoutInitLoading(true);
    Promise.all([API.get("/auth/me"), API.get("/auth/coupons"), API.get("/products")])
      .then(([meRes, couponsRes, productsRes]) => {
        const userAddresses = meRes.data?.user?.addresses || [];
        setAddresses(userAddresses);
        const defaultAddress = userAddresses.find((a) => a.isDefault);
        setSelectedAddressId(defaultAddress?._id || userAddresses[0]?._id || "");
        setAddressForm((prev) => ({
          ...prev,
          fullName: meRes.data?.user?.name || prev.fullName,
          phone: meRes.data?.user?.phone || prev.phone
        }));
        setAvailableCoupons(couponsRes.data?.coupons || []);
        const stockMap = {};
        (productsRes.data || []).forEach((product) => {
          stockMap[String(product._id)] = Number(product.stock || 0);
        });
        setStockByProductId(stockMap);
      })
      .catch(() => {
        setAddresses([]);
        setAvailableCoupons([]);
      })
      .finally(() => setCheckoutInitLoading(false));
  }, []);

  const stockIssueForItem = (item) => {
    const stock = Number(stockByProductId[item.productId] ?? Infinity);
    if (!Number.isFinite(stock)) return null;
    if (stock <= 0) return "Out of stock";
    if (item.quantity > stock) return `Only ${stock} left`;
    if (stock <= 2) return `Only ${stock} left`;
    return null;
  };

  const sameCartLine = (a, b) =>
    a.productId === b.productId &&
    (a.size || "") === (b.size || "") &&
    (a.color || "") === (b.color || "") &&
    (a.packLabel || "") === (b.packLabel || "");

  const cartLineKey = (item) =>
    [item.productId, item.size || "", item.color || "", item.packLabel || ""].join("::");

  const updateQty = (line, delta) => {
    setCart((prev) => {
      return prev
        .map((item) => {
          if (!sameCartLine(item, line)) return item;
          const stock = Number(stockByProductId[item.productId] ?? Infinity);
          const nextQty = Math.max(1, item.quantity + delta);
          if (Number.isFinite(stock) && nextQty > stock) {
            setMessage(`Only ${stock} left for ${item.name}`);
            return item;
          }
          return { ...item, quantity: nextQty };
        })
        .filter((item) => item.quantity > 0);
    });
  };

  const removeItem = (line) => {
    setCart((prev) => prev.filter((item) => !sameCartLine(item, line)));
  };

  const saveForLater = (item) => {
    setCart((prev) => prev.filter((p) => !sameCartLine(p, item)));
    setWishlist((prev) => {
      const exists = prev.some((p) => p._id === item.productId);
      if (exists) return prev;
      return [...prev, { _id: item.productId, name: item.name, price: item.price, image: item.image || "" }];
    });
    setMessage(`${item.name} moved to wishlist`);
  };

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    const coupon = availableCoupons.find((item) => item.code === code && item.isActive);
    if (!coupon) {
      setMessage("Invalid or expired coupon code");
      return;
    }
    if (subtotal < Number(coupon.minOrderAmount || 0)) {
      setMessage(`Minimum order amount ₹ ${coupon.minOrderAmount} required`);
      return;
    }
    setAppliedCoupon(coupon);
    setMessage(`Coupon ${coupon.code} applied`);
  };

  const onAddressFieldChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  const detectCheckoutLocation = () => {
    if (!navigator.geolocation) {
      setMessage("Geolocation is not supported in this browser.");
      return;
    }
    setMessage("Detecting location...");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const next = {
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6),
          accuracy: Math.round(accuracy || 0)
        };
        setAddressForm((prev) => ({ ...prev, ...next }));
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const addr = data?.address || {};
          const city = addr.city || addr.town || addr.village || addr.county || "";
          const state = addr.state || "";
          const pincode = addr.postcode || "";
          const road = [addr.house_number, addr.road].filter(Boolean).join(" ");
          const area = [addr.suburb, addr.neighbourhood].filter(Boolean).join(", ");
          setAddressForm((prev) => ({
            ...prev,
            line1: prev.line1 || road || prev.line1,
            line2: prev.line2 || area || prev.line2,
            city: prev.city || city,
            state: prev.state || state,
            pincode: prev.pincode || pincode,
            locationLabel: data?.display_name || prev.locationLabel
          }));
          setMessage("Location detected.");
        } catch (_error) {
          setMessage("Coordinates captured, but address lookup failed.");
        }
      },
      (error) => {
        setMessage(error?.message || "Failed to detect location.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const addAddressInCheckout = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...addressForm,
        latitude: addressForm.latitude ? Number(addressForm.latitude) : null,
        longitude: addressForm.longitude ? Number(addressForm.longitude) : null,
        accuracy: addressForm.accuracy ? Number(addressForm.accuracy) : null
      };
      const { data } = await API.post("/auth/addresses", payload);
      const list = data.addresses || [];
      setAddresses(list);
      setSelectedAddressId(list[list.length - 1]?._id || "");
      setShowAddressForm(false);
      setAddressForm((prev) => ({ ...prev, line1: "", line2: "", city: "", state: "", pincode: "", latitude: "", longitude: "", accuracy: "", locationLabel: "" }));
      setMessage("Address added successfully.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to add address.");
    }
  };

  const deleteCheckoutAddress = async (addressId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await API.delete(`/auth/addresses/${addressId}`);
      const list = data.addresses || [];
      setAddresses(list);
      if (selectedAddressId === addressId) {
        setSelectedAddressId(list.find((a) => a.isDefault)?._id || list[0]?._id || "");
      }
      setMessage("Address removed.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Could not remove address.");
    }
  };

  const setDefaultCheckoutAddress = async (addressId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await API.put(`/auth/addresses/${addressId}/default`);
      const list = data.addresses || [];
      setAddresses(list);
      setSelectedAddressId(addressId);
      setMessage("Default address updated.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Could not update default.");
    }
  };

  const payNow = async () => {
    try {
      if (!cart.length) return;
      const deliveryAddress = addresses.find((a) => a._id === selectedAddressId);
      if (!deliveryAddress) {
        setMessage("Please select a delivery address before payment.");
        return;
      }
      const hasUnavailable = cart.some((item) => {
        const stock = Number(stockByProductId[item.productId] ?? Infinity);
        return Number.isFinite(stock) && (stock <= 0 || item.quantity > stock);
      });
      if (hasUnavailable) {
        setMessage("Please resolve stock issues before checkout.");
        return;
      }
      setLoading(true);
      setMessage("");
      const orderProducts = cart.map((item) => ({
        productId: item.productId,
        name: item.name,
        price: Number(item.price),
        quantity: Number(item.quantity),
        image: item.image || "",
        size: item.size || "",
        color: item.color || "",
        packLabel: String(item.packLabel || "").trim(),
        customText: String(item.customText || "").trim(),
        customImage: String(item.customImage || "").trim()
      }));
      const orderRes = await API.post("/orders", {
        products: orderProducts,
        totalAmount: total,
        taxAmount,
        shippingAmount: shippingCharged,
        paymentMethod,
        specialInstructions,
        gstNumber,
        panNumber,
        couponCode: appliedCoupon?.code || "",
        discountAmount,
        deliveryAddress
      });
      const appOrderId = orderRes.data._id;

      if (paymentMethod === "Cash On Delivery") {
        setCart([]);
        await clearServerAndLocalCart();
        navigate(`/orders/success/${appOrderId}`, { replace: true });
        return;
      }

      if (!window.Razorpay) {
        setMessage("Razorpay SDK not loaded. Please refresh and try again.");
        return;
      }

      const razorRes = await API.post("/payment/create-order", { amount: total, appOrderId });
      const configRes = await API.get("/payment/config");
      const order = razorRes.data;
      const key = configRes.data?.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!key) {
        setMessage("Razorpay key not configured. Please contact admin.");
        return;
      }
      const options = {
        key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: "CORRUCRAFT INDUSTRIES",
        description: "Order payment",
        handler: async function (response) {
          await API.post("/payment/verify", {
            appOrderId,
            ...response
          });
          setCart([]);
          await clearServerAndLocalCart();
          navigate(`/orders/success/${appOrderId}`, { replace: true });
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => {
        const reason = response?.error?.description || response?.error?.reason || "Payment failed. Please try again.";
        setMessage(reason);
      });
      razorpay.open();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedAddress = addresses.find((a) => a._id === selectedAddressId);
  const hasOutOfStock = cart.some((item) => {
    const stock = Number(stockByProductId[item.productId] ?? Infinity);
    return Number.isFinite(stock) && (stock <= 0 || item.quantity > stock);
  });
  const deliveryEstimate = useMemo(() => {
    if (!selectedAddress) return "";
    const baseDays = selectedAddress.pincode ? (Number(String(selectedAddress.pincode).slice(-1)) % 3) + 3 : 4;
    const estimate = new Date();
    estimate.setDate(estimate.getDate() + baseDays);
    return estimate.toLocaleDateString();
  }, [selectedAddress]);

  const payDisabled = !cart.length || !selectedAddress || loading || hasOutOfStock;
  const payLabel = loading ? "Processing…" : paymentMethod === "Online" ? "Pay securely" : "Place order";

  return (
    <div className="checkout-premium">
      <button type="button" className="checkout-premium__back" onClick={() => navigate("/")}>
        ← Continue shopping
      </button>
      {message ? (
        <div className="checkout-premium__flash" role="status">
          {message}
        </div>
      ) : null}

      <div className="checkout-premium__grid">
        <div className="checkout-premium__main">
          <section className="checkout-premium-glass checkout-premium-cart">
            <header className="checkout-premium-cart__head">
              <div>
                <h1 className="checkout-premium-h1">Shopping cart</h1>
                <p className="checkout-premium-muted">{cart.length} {cart.length === 1 ? "item" : "items"}</p>
              </div>
              {savingsHighlight > 0 ? (
                <div className="checkout-premium-saved">
                  You saved <span>₹{savingsHighlight.toFixed(2)}</span>
                </div>
              ) : null}
            </header>

            {subtotal > 0 ? (
              <div className="checkout-premium-freeship">
                <div className="checkout-premium-freeship__row">
                  <span>{freeShippingUnlocked ? "Free shipping unlocked" : `Add ₹${freeShipRemaining.toFixed(0)} more for free shipping`}</span>
                  <span className="checkout-premium-muted">{Math.round(freeShipProgress)}%</span>
                </div>
                <div className="checkout-premium-freeship__track" aria-hidden>
                  <div className="checkout-premium-freeship__fill" style={{ width: `${freeShipProgress}%` }} />
                </div>
                <p className="checkout-premium-muted checkout-premium-freeship__hint">On orders of ₹{FREE_SHIPPING_THRESHOLD}+ (standard delivery).</p>
              </div>
            ) : null}

            <hr className="checkout-premium-divider" />

            {checkoutInitLoading ? (
              <div className="checkout-premium-skeleton-list" aria-busy="true" aria-label="Loading cart">
                {[0, 1, 2].map((sk) => (
                  <div key={sk} className="checkout-premium-skeleton-item">
                    <div className="checkout-premium-skeleton checkout-premium-skeleton--img" />
                    <div className="checkout-premium-skeleton-col">
                      <div className="checkout-premium-skeleton checkout-premium-skeleton--line" />
                      <div className="checkout-premium-skeleton checkout-premium-skeleton--line checkout-premium-skeleton--short" />
                      <div className="checkout-premium-skeleton checkout-premium-skeleton--line checkout-premium-skeleton--short" />
                    </div>
                  </div>
                ))}
              </div>
            ) : cart.length === 0 ? (
              <div className="checkout-premium-empty">
                <p>Your cart is empty.</p>
                <button type="button" className="checkout-premium-btn checkout-premium-btn--primary" onClick={() => navigate("/")}>
                  Browse products
                </button>
              </div>
            ) : (
              <ul className="checkout-premium-items">
                {cart.map((item) => {
                  const stock = Number(stockByProductId[item.productId] ?? Infinity);
                  const atMax = Number.isFinite(stock) && item.quantity >= stock;
                  const atMin = item.quantity <= 1;
                  const lineTotal = (Number(item.price) * Number(item.quantity)).toFixed(2);
                  return (
                    <li key={cartLineKey(item)} className="checkout-premium-item">
                      <div className="checkout-premium-item__media">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="checkout-premium-item__img" />
                        ) : (
                          <span className="checkout-premium-item__placeholder">{item.name.slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="checkout-premium-item__detail">
                        <h2 className="checkout-premium-item__title">{item.name}</h2>
                        <p className="checkout-premium-item__variant">
                          <span>Size: {item.size || "Standard"}</span>
                          <span className="checkout-premium-item__dot" />
                          <span>Color: {item.color || "Default"}</span>
                          {item.packLabel ? (
                            <>
                              <span className="checkout-premium-item__dot" />
                              <span>Pack: {item.packLabel}</span>
                            </>
                          ) : null}
                        </p>
                        {stockIssueForItem(item) ? <p className="checkout-premium-stock">{stockIssueForItem(item)}</p> : null}
                      </div>
                      <p className="checkout-premium-item__unit">
                        <span className="checkout-premium-muted checkout-premium-item__unit-label">Price</span>
                        <span>₹{Number(item.price).toFixed(2)}</span>
                      </p>
                      <div className="checkout-premium-item__qty">
                        <span className="checkout-premium-muted checkout-premium-item__unit-label">Qty</span>
                        <div className="checkout-premium-qty">
                          <button
                            type="button"
                            className="checkout-premium-qty__btn"
                            disabled={atMin}
                            aria-label="Decrease quantity"
                            onClick={() => updateQty(item, -1)}
                          >
                            −
                          </button>
                          <span className="checkout-premium-qty__val">{item.quantity}</span>
                          <button
                            type="button"
                            className="checkout-premium-qty__btn"
                            disabled={atMax}
                            aria-label="Increase quantity"
                            onClick={() => updateQty(item, 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="checkout-premium-item__line">
                        <span className="checkout-premium-muted checkout-premium-item__unit-label">Line total</span>
                        <span className="checkout-premium-item__line-total">₹{lineTotal}</span>
                        <div className="checkout-premium-item__actions">
                          <button type="button" className="checkout-premium-link checkout-premium-link--danger" onClick={() => removeItem(item)}>
                            Remove
                          </button>
                          <button type="button" className="checkout-premium-btn checkout-premium-btn--ghost" onClick={() => saveForLater(item)}>
                            Save for later
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="checkout-premium-glass checkout-premium-address-section">
            <header className="checkout-premium-address-section__head">
              <div>
                <h2 className="checkout-premium-h2">Delivery address</h2>
                {deliveryEstimate ? (
                  <p className="checkout-premium-delivery-est">
                    Estimated delivery by <strong>{deliveryEstimate}</strong>
                  </p>
                ) : (
                  <p className="checkout-premium-muted">Select an address to see delivery estimate.</p>
                )}
              </div>
              <button type="button" className="checkout-premium-btn checkout-premium-btn--primary checkout-premium-btn--sm" onClick={() => setShowAddressForm((v) => !v)}>
                {showAddressForm ? "Close form" : "Add new address"}
              </button>
            </header>
            <hr className="checkout-premium-divider" />
            {addresses.length ? (
              <div className="checkout-premium-addresses">
                {addresses.map((address) => {
                  const selected = selectedAddressId === address._id;
                  const mapHref =
                    address.latitude && address.longitude
                      ? `https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`
                      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          [address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(", ")
                        )}`;
                  return (
                    <label
                      key={address._id}
                      className={`checkout-premium-address ${selected ? "is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="selectedAddress"
                        className="checkout-premium-sr-only"
                        checked={selected}
                        onChange={() => setSelectedAddressId(address._id)}
                      />
                      <div className="checkout-premium-address__body">
                        <div className="checkout-premium-address__row">
                          <span className="checkout-premium-address__name">{address.fullName}</span>
                          {address.isDefault ? <span className="checkout-premium-badge">Default</span> : null}
                        </div>
                        <p className="checkout-premium-address__phone">{address.phone}</p>
                        <p className="checkout-premium-address__lines">
                          {address.line1}
                          {address.line2 ? `, ${address.line2}` : ""}, {address.city}, {address.state} — {address.pincode}
                        </p>
                        <a className="checkout-premium-maplink" href={mapHref} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                          View on map
                        </a>
                      </div>
                      <div className="checkout-premium-address__tools">
                        {!address.isDefault ? (
                          <button
                            type="button"
                            className="checkout-premium-iconbtn"
                            title="Set as default"
                            onClick={(e) => setDefaultCheckoutAddress(address._id, e)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="checkout-premium-iconbtn checkout-premium-iconbtn--danger"
                          title="Remove address"
                          onClick={(e) => deleteCheckoutAddress(address._id, e)}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" />
                          </svg>
                        </button>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="checkout-premium-muted">No saved addresses yet. Add one to continue.</p>
            )}
            {showAddressForm ? (
              <form className="checkout-premium-form" onSubmit={addAddressInCheckout}>
                <div className="checkout-premium-form__grid2">
                  <input className="checkout-premium-input" name="fullName" value={addressForm.fullName} onChange={onAddressFieldChange} placeholder="Full name" required />
                  <input className="checkout-premium-input" name="phone" value={addressForm.phone} onChange={onAddressFieldChange} placeholder="Phone number" required />
                </div>
                <input className="checkout-premium-input" name="line1" value={addressForm.line1} onChange={onAddressFieldChange} placeholder="Address line 1" required />
                <input className="checkout-premium-input" name="line2" value={addressForm.line2} onChange={onAddressFieldChange} placeholder="Address line 2 (optional)" />
                <div className="checkout-premium-form__grid2">
                  <input className="checkout-premium-input" name="city" value={addressForm.city} onChange={onAddressFieldChange} placeholder="City" required />
                  <input className="checkout-premium-input" name="state" value={addressForm.state} onChange={onAddressFieldChange} placeholder="State" required />
                </div>
                <input className="checkout-premium-input" name="pincode" value={addressForm.pincode} onChange={onAddressFieldChange} placeholder="Pincode" required />
                <div className="checkout-premium-form__actions">
                  <button type="button" className="checkout-premium-btn checkout-premium-btn--ghost" onClick={detectCheckoutLocation}>
                    Auto-detect location
                  </button>
                  <button type="submit" className="checkout-premium-btn checkout-premium-btn--primary">
                    Save address
                  </button>
                </div>
              </form>
            ) : null}
          </section>
        </div>

        <aside className="checkout-premium__aside">
          <div className={`checkout-premium-glass checkout-premium-summary ${mobileSummaryOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="checkout-premium-summary__toggle"
              aria-expanded={mobileSummaryOpen}
              onClick={() => setMobileSummaryOpen((v) => !v)}
            >
              <span>Order summary</span>
              <span className="checkout-premium-summary__chev" aria-hidden>
                {mobileSummaryOpen ? "▲" : "▼"}
              </span>
              <span className="checkout-premium-summary__toggle-total">₹{total.toFixed(2)}</span>
            </button>

            <div className="checkout-premium-summary__body">
              <h2 className="checkout-premium-h2 checkout-premium-summary__title">Order summary</h2>

              <div className="checkout-premium-field">
                <label className="checkout-premium-label" htmlFor="coupon-input">
                  Apply coupon
                </label>
                <div className="checkout-premium-coupon">
                  <input
                    id="coupon-input"
                    className="checkout-premium-input"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    placeholder="Enter code"
                  />
                  <button type="button" className="checkout-premium-btn checkout-premium-btn--primary checkout-premium-btn--sm" onClick={applyCoupon}>
                    Apply
                  </button>
                </div>
                {appliedCoupon ? (
                  <p className="checkout-premium-coupon-applied">
                    Applied <strong>{appliedCoupon.code}</strong> — {appliedCoupon.discountType} {appliedCoupon.discountValue}
                  </p>
                ) : null}
              </div>

              <hr className="checkout-premium-divider" />

              <div className="checkout-premium-field">
                <label className="checkout-premium-label" htmlFor="special-notes">
                  Special instructions
                </label>
                <textarea
                  id="special-notes"
                  className="checkout-premium-textarea"
                  rows={3}
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Delivery notes, gate codes, fragile items…"
                />
              </div>

              <hr className="checkout-premium-divider" />

              <dl className="checkout-premium-breakdown">
                <div className="checkout-premium-breakdown__row">
                  <dt>Subtotal</dt>
                  <dd>₹{subtotal.toFixed(2)}</dd>
                </div>
                <div className="checkout-premium-breakdown__row">
                  <dt>Discount</dt>
                  <dd className={discountAmount > 0 ? "is-accent" : ""}>− ₹{discountAmount.toFixed(2)}</dd>
                </div>
                <div className="checkout-premium-breakdown__row">
                  <dt>Tax ({Number(pricingConfig.taxPercent || 0)}%)</dt>
                  <dd>₹{taxAmount.toFixed(2)}</dd>
                </div>
                <div className="checkout-premium-breakdown__row">
                  <dt>Shipping</dt>
                  <dd>
                    {freeShippingUnlocked && baseShippingFee > 0 ? (
                      <>
                        <span className="checkout-premium-struck">₹{baseShippingFee.toFixed(2)}</span> ₹{shippingCharged.toFixed(2)}
                      </>
                    ) : (
                      <>₹{shippingCharged.toFixed(2)}</>
                    )}
                  </dd>
                </div>
              </dl>

              <hr className="checkout-premium-divider" />

              <div className="checkout-premium-total-row">
                <span>Total</span>
                <strong>₹{total.toFixed(2)}</strong>
              </div>

              <div className="checkout-premium-field">
                <label className="checkout-premium-label" htmlFor="gst-in">
                  GST number <span className="checkout-premium-muted">(optional)</span>
                </label>
                <input id="gst-in" className="checkout-premium-input" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} placeholder="GSTIN" />
              </div>
              <div className="checkout-premium-field">
                <label className="checkout-premium-label" htmlFor="pan-in">
                  PAN <span className="checkout-premium-muted">(optional)</span>
                </label>
                <input id="pan-in" className="checkout-premium-input" value={panNumber} onChange={(e) => setPanNumber(e.target.value)} placeholder="PAN" />
              </div>

              <hr className="checkout-premium-divider" />

              <p className="checkout-premium-label">Payment method</p>
              <div className="checkout-premium-payopts" role="radiogroup" aria-label="Payment method">
                <label className={`checkout-premium-payopt ${paymentMethod === "Cash On Delivery" ? "is-on" : ""}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="Cash On Delivery"
                    className="checkout-premium-sr-only"
                    checked={paymentMethod === "Cash On Delivery"}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span className="checkout-premium-payopt__title">Cash on delivery</span>
                  <span className="checkout-premium-payopt__sub">Pay when your order arrives</span>
                </label>
                <label className={`checkout-premium-payopt ${paymentMethod === "Online" ? "is-on" : ""}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="Online"
                    className="checkout-premium-sr-only"
                    checked={paymentMethod === "Online"}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span className="checkout-premium-payopt__title">Online payment</span>
                  <span className="checkout-premium-payopt__sub">Razorpay — UPI, cards & wallets</span>
                  <span className="checkout-premium-payicons" aria-hidden>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="checkout-premium-payicons__svg">
                      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="checkout-premium-payicons__svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                    </svg>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="checkout-premium-payicons__svg">
                      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M18 12a3 3 0 1 0 0 6h3v-6h-3z" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </span>
                </label>
              </div>

              {hasOutOfStock ? <p className="checkout-premium-stock">Some items are out of stock or exceed available quantity.</p> : null}

              <div className="checkout-premium-trust">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" aria-hidden>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>Secure checkout · Encrypted payments</span>
              </div>

              <button type="button" className="checkout-premium-btn checkout-premium-btn--primary checkout-premium-btn--block checkout-premium-summary__cta" onClick={payNow} disabled={payDisabled}>
                {payLabel}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <div className="checkout-premium-mobilebar" role="region" aria-label="Checkout actions">
        <button type="button" className="checkout-premium-mobilebar__summary" onClick={() => setMobileSummaryOpen((v) => !v)}>
          <span className="checkout-premium-mobilebar__label">Total</span>
          <span className="checkout-premium-mobilebar__amount">₹{total.toFixed(2)}</span>
          <span className="checkout-premium-mobilebar__hint">{mobileSummaryOpen ? "Hide detail" : "View summary"}</span>
        </button>
        <button type="button" className="checkout-premium-btn checkout-premium-btn--primary checkout-premium-mobilebar__pay" onClick={payNow} disabled={payDisabled}>
          {payLabel}
        </button>
      </div>
    </div>
  );
};

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [message, setMessage] = useState("");
  const steps = ["Placed", "Order Confirmed", "Shipped", "Out For Delivery", "Delivered"];

  useEffect(() => {
    API.get(`/orders/my-orders/${orderId}`)
      .then((res) => setOrder(res.data))
      .catch(() => setMessage("Unable to load order details."));
  }, [orderId]);

  const statusIndex = Math.max(steps.indexOf(order?.status || "Placed"), 0);

  return (
    <div className="card order-success">
      <div className="success-badge">Payment Success - Congratulations your order got placed</div>
      <div className="order-steps">
        {steps.map((step, idx) => (
          <div key={step} className={`order-step ${idx <= statusIndex ? "active" : ""}`}>
            <span className="step-dot">{idx + 1}</span>
            <small>{step}</small>
          </div>
        ))}
      </div>
      {order && (
        <div className="order-items-list">
          {order.products.map((item, i) => (
            <div key={`${item.productId}-${i}`} className="order-item-row">
              <div className="order-item-left">
                <div className="order-item-img">
                  {item.image ? <img src={item.image} alt={item.name} /> : <span>{item.name.slice(0, 2).toUpperCase()}</span>}
                </div>
                <div>
                  <strong>{item.name}</strong>
                  <p>Qty: {item.quantity}</p>
                  <p>₹ {item.price}</p>
                  {orderLineExtras(item).map((line, li) => (
                    <p key={`${i}-ex-${li}`} className="order-item-extra">
                      {line}
                    </p>
                  ))}
                  {item.customImage?.trim() ? (
                    <a className="order-custom-thumb-link" href={item.customImage.trim()} target="_blank" rel="noreferrer">
                      <img className="order-custom-thumb" src={item.customImage.trim()} alt="Customer upload" />
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="order-item-right">
                <strong>{order.deliveryAddress?.fullName}</strong>
                <p>
                  {order.deliveryAddress?.city}, {order.deliveryAddress?.state}, {order.deliveryAddress?.pincode}
                </p>
                <p>Phone: {order.deliveryAddress?.phone}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {message && <small>{message}</small>}
    </div>
  );
};

function ProfileNavIcon({ name }) {
  const s = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
  };
  switch (name) {
    case "user":
      return (
        <svg {...s}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "package":
      return (
        <svg {...s}>
          <path d="M16.5 9.4 7.5 4.21" />
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <path d="M3.27 6.96 12 12.01l8.73-5.05" />
        </svg>
      );
    case "mapPin":
      return (
        <svg {...s}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case "heart":
      return (
        <svg {...s}>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case "cart":
      return (
        <svg {...s}>
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
      );
    case "shield":
      return (
        <svg {...s}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case "ticket":
      return (
        <svg {...s}>
          <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
          <path d="M13 5v2M13 17v2M13 11v2" />
        </svg>
      );
    case "bell":
      return (
        <svg {...s}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    case "chart":
      return (
        <svg {...s}>
          <path d="M3 3v18h18" />
          <path d="m19 9-5 5-4-4-3 3" />
        </svg>
      );
    case "sparkles":
      return (
        <svg {...s}>
          <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z" />
        </svg>
      );
    case "star":
      return (
        <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      );
    case "key":
      return (
        <svg {...s}>
          <circle cx="7.5" cy="15.5" r="5.5" />
          <path d="m21 2-9.6 9.6M15.5 7.5l3 3L22 7l-3-3" />
        </svg>
      );
    case "logout":
      return (
        <svg {...s}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      );
    default:
      return null;
  }
}

const PROFILE_NAV_ITEMS = [
  { id: "account", label: "Account", icon: "user" },
  { id: "orders", label: "Orders", icon: "package" },
  { id: "address", label: "Addresses", icon: "mapPin" },
  { id: "wishlist", label: "Wishlist", icon: "heart" },
  { id: "cart", label: "Saved cart", icon: "cart" },
  { id: "security", label: "Security", icon: "shield" },
  { id: "coupons", label: "Coupons", icon: "ticket" },
  { id: "notifications", label: "Alerts", icon: "bell" },
  { id: "insights", label: "Insights", icon: "chart" },
  { id: "recommendations", label: "For you", icon: "sparkles" },
  { id: "reviews", label: "Reviews", icon: "star" },
  { id: "password", label: "Password", icon: "key" }
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("account");
  const [profile, setProfile] = useState({ name: "", email: "", phone: "", id: "", addresses: [], paymentMethods: [] });
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [message, setMessage] = useState("");
  const [profileToast, setProfileToast] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [myOrders, setMyOrders] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [insights, setInsights] = useState({ totalSpent: 0, favoriteCategories: [], purchaseHistory: [] });
  const [recommendations, setRecommendations] = useState([]);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [cartSyncItems, setCartSyncItems] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ type: "CARD", label: "", isDefault: false });
  const [wishlist] = useState(() => JSON.parse(localStorage.getItem("wishlist") || "[]"));
  const [addressForm, setAddressForm] = useState({
    fullName: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: ""
  });

  const fetchProfile = async () => {
    try {
      const { data } = await API.get("/auth/me");
      setProfile(data.user);
      setName(data.user.name || "");
      setPhone(data.user.phone || "");
      setGender(data.user.gender || "");
      setDob(data.user.dob ? new Date(data.user.dob).toISOString().slice(0, 10) : "");
      setProfilePicture(data.user.profilePicture || "");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to load profile");
    }
  };

  const loadProfileExtras = async () => {
    try {
      const [ordersRes, reviewsRes, insightsRes, recRes, couponsRes, cartRes, paymentsRes] = await Promise.all([
        API.get("/orders/my-orders"),
        API.get("/reviews/my"),
        API.get("/auth/insights"),
        API.get("/auth/recommendations"),
        API.get("/auth/coupons"),
        API.get("/auth/cart-sync"),
        API.get("/auth/payment-methods")
      ]);
      setMyOrders(ordersRes.data || []);
      setMyReviews(reviewsRes.data?.reviews || []);
      setInsights(insightsRes.data || { totalSpent: 0, favoriteCategories: [], purchaseHistory: [] });
      setRecommendations(recRes.data?.products || []);
      setAvailableCoupons(couponsRes.data?.coupons || []);
      setCartSyncItems(cartRes.data?.cart || []);
      setProfile((prev) => ({ ...prev, paymentMethods: paymentsRes.data?.paymentMethods || prev.paymentMethods || [] }));
    } catch (error) {
      setMessage(error?.response?.data?.message || "Some profile modules failed to load");
    }
  };

  useEffect(() => {
    fetchProfile();
    loadProfileExtras();
  }, []);

  useEffect(() => {
    if (!message) return;
    const id = window.setTimeout(() => setMessage(""), 3500);
    return () => window.clearTimeout(id);
  }, [message]);

  const saveProfile = async () => {
    try {
      const { data } = await API.put("/auth/me", { name, phone, gender, dob: dob || null, profilePicture });
      setProfile(data.user);
      setProfilePicture(data.user?.profilePicture || profilePicture);
      localStorage.setItem("user", JSON.stringify(data.user));
      setEditMode(false);
      setProfileToast({ type: "success", text: "Profile saved successfully" });
      window.setTimeout(() => setProfileToast(null), 3600);
    } catch (error) {
      setMessage(error?.response?.data?.message || "Profile update failed");
    }
  };

  const savePreferences = async (prefs) => {
    try {
      const { data } = await API.put("/auth/preferences", prefs);
      setProfile(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      setMessage("Preferences updated");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to update preferences");
    }
  };

  const changeEmailAction = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.put("/auth/change-email", { newEmail, password: emailPassword });
      setProfile(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      setNewEmail("");
      setEmailPassword("");
      setMessage("Email updated");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Email change failed");
    }
  };

  const logoutAllDevices = async () => {
    try {
      await syncCartToServerBeforeLogout();
      await API.post("/auth/logout-all");
      persistLogoutCartCleanup();
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/auth", { replace: true });
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to logout all devices");
    }
  };

  const syncCurrentCart = async () => {
    try {
      const localCart = readPersistedCart();
      const { data } = await API.put("/auth/cart-sync", { cart: localCart });
      setCartSyncItems(data.cart || []);
      setMessage("Cart synced across devices");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Cart sync failed");
    }
  };

  const addPaymentMethod = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/auth/payment-methods", paymentForm);
      setProfile((prev) => ({ ...prev, paymentMethods: data.paymentMethods || [] }));
      setPaymentForm({ type: "CARD", label: "", isDefault: false });
      setMessage("Payment method saved");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to save payment method");
    }
  };

  const removePaymentMethod = async (methodId) => {
    const { data } = await API.delete(`/auth/payment-methods/${methodId}`);
    setProfile((prev) => ({ ...prev, paymentMethods: data.paymentMethods || [] }));
  };

  const setDefaultPaymentMethod = async (methodId) => {
    const { data } = await API.put(`/auth/payment-methods/${methodId}/default`);
    setProfile((prev) => ({ ...prev, paymentMethods: data.paymentMethods || [] }));
  };

  const downloadInvoice = async (orderId) => {
    try {
      const { data } = await API.get(`/orders/my-orders/${orderId}/invoice`);
      const lines = [
        `Invoice: ${data.invoiceNo}`,
        `Date: ${new Date(data.issueDate).toLocaleString()}`,
        `Customer: ${data.order.userId?.name || data.order.deliveryAddress?.fullName || "Customer"}`,
        `Payment: ${data.order.paymentMethod}`,
        `Amount: ₹ ${data.order.totalAmount}`,
        ""
      ];
      (data.order.products || []).forEach((item) => lines.push(`${item.name} x ${item.quantity} = ₹ ${item.price * item.quantity}`));
      const w = window.open("", "_blank");
      w.document.write(`<pre>${lines.join("\n")}</pre>`);
      w.document.close();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to generate invoice");
    }
  };

  const deleteReview = async (reviewId) => {
    await API.delete(`/reviews/${reviewId}`);
    setMyReviews((prev) => prev.filter((item) => item._id !== reviewId));
  };

  const requestReturn = async (orderId) => {
    const reason = window.prompt("Please enter return reason:");
    if (!reason) return;
    const { data } = await API.post(`/orders/my-orders/${orderId}/return`, { reason });
    setMyOrders((prev) => prev.map((item) => (item._id === orderId ? data : item)));
  };

  const cancelReturnRequest = async (orderId) => {
    const ok = window.confirm("Cancel the return request?");
    if (!ok) return;
    const { data } = await API.put(`/orders/my-orders/${orderId}/return/cancel`);
    setMyOrders((prev) => prev.map((item) => (item._id === orderId ? data : item)));
  };

  const cancelOrder = async (orderId) => {
    const ok = window.confirm("Cancel this order?");
    if (!ok) return;
    const { data } = await API.put(`/orders/my-orders/${orderId}/cancel`);
    setMyOrders((prev) => prev.map((item) => (item._id === orderId ? data : item)));
  };

  const onAddressChange = (e) => {
    const { name: field, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [field]: value }));
  };

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/auth/addresses", addressForm);
      setProfile((prev) => ({ ...prev, addresses: data.addresses }));
      setAddressForm({ fullName: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
      setMessage("Shipping address added.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to add address");
    }
  };

  const deleteAddress = async (addressId) => {
    const { data } = await API.delete(`/auth/addresses/${addressId}`);
    setProfile((prev) => ({ ...prev, addresses: data.addresses }));
  };

  const setDefaultAddress = async (addressId) => {
    const { data } = await API.put(`/auth/addresses/${addressId}/default`);
    setProfile((prev) => ({ ...prev, addresses: data.addresses }));
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage("New password and confirm password do not match");
      return;
    }
    try {
      const { data } = await API.put("/auth/change-password", { currentPassword, newPassword });
      setMessage(data.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setActiveTab("account");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to change password");
    }
  };

  const logout = async () => {
    await syncCartToServerBeforeLogout();
    persistLogoutCartCleanup();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/auth", { replace: true });
  };

  const referralCode = `C${String(profile.id || "").slice(-6).toUpperCase() || "000000"}`;

  const profileCompletion = useMemo(() => {
    let n = 0;
    const t = 5;
    if (String(name || "").trim()) n += 1;
    if (String(phone || "").trim()) n += 1;
    if (gender) n += 1;
    if (dob) n += 1;
    if (String(profilePicture || profile.profilePicture || "").trim()) n += 1;
    return Math.round((n / t) * 100);
  }, [name, phone, gender, dob, profilePicture, profile.profilePicture]);

  const copyReferralCode = () => {
    navigator.clipboard?.writeText(referralCode).then(
      () => {
        setProfileToast({ type: "success", text: "Referral code copied" });
        window.setTimeout(() => setProfileToast(null), 2600);
      },
      () => setMessage("Could not copy to clipboard")
    );
  };

  const onAvatarFile = (e) => {
    const file = e.target.files?.[0];
    if (!file?.type?.startsWith("image/")) return;
    if (file.size > 900 * 1024) {
      setMessage("Please use an image under 900KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setProfilePicture(String(reader.result));
      setEditMode(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="profile-dash">
      <div className="profile-dash-bg" aria-hidden>
        <div className="profile-dash-orb profile-dash-orb--1" />
        <div className="profile-dash-orb profile-dash-orb--2" />
        <div className="profile-dash-lines" />
      </div>

      {profileToast && (
        <div className={`profile-dash-toast profile-dash-toast--${profileToast.type}`} role="status">
          {profileToast.text}
        </div>
      )}

      <div className="profile-dash-inner">
        <aside className="profile-dash-sidebar">
          <div className="profile-dash-sidebar-inner">
            <p className="profile-dash-sidebar-title">Menu</p>
            <nav className="profile-dash-nav">
              {PROFILE_NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`profile-dash-nav-btn ${activeTab === item.id ? "is-active" : ""}`}
                  onClick={() => setActiveTab(item.id)}
                >
                  <span className="profile-dash-nav-icon">
                    <ProfileNavIcon name={item.icon} />
                  </span>
                  <span className="profile-dash-nav-label">{item.label}</span>
                </button>
              ))}
            </nav>
            <button type="button" className="profile-dash-nav-btn profile-dash-nav-btn--logout" onClick={logout}>
              <span className="profile-dash-nav-icon">
                <ProfileNavIcon name="logout" />
              </span>
              <span className="profile-dash-nav-label">Log out</span>
            </button>
          </div>
        </aside>

        <section className="profile-dash-main">
          {activeTab === "account" ? (
            <>
              <header className="profile-dash-header">
                <div className="profile-dash-avatar-wrap">
                  <div className="profile-dash-avatar-ring">
                    {profilePicture || profile.profilePicture ? (
                      <img src={profilePicture || profile.profilePicture} alt="" />
                    ) : (
                      <span className="profile-dash-avatar-fallback" aria-hidden>
                        👤
                      </span>
                    )}
                  </div>
                  <input id="profile-avatar-file" type="file" accept="image/*" className="profile-dash-file-input" onChange={onAvatarFile} />
                </div>
                <div className="profile-dash-header-text">
                  <h1 className="profile-dash-name">{profile.name || "Member"}</h1>
                  <p className="profile-dash-subtitle">Premium Member · Shop smarter</p>
                  <div className="profile-dash-progress">
                    <div className="profile-dash-progress-track">
                      <div className="profile-dash-progress-fill" style={{ width: `${profileCompletion}%` }} />
                    </div>
                    <span className="profile-dash-progress-label">{profileCompletion}% profile complete</span>
                  </div>
                </div>
                <div className="profile-dash-header-actions">
                  <label htmlFor="profile-avatar-file" className="profile-dash-btn-upload">
                    Upload photo
                  </label>
                  <button type="button" className="profile-dash-btn-edit" onClick={() => setEditMode((v) => !v)}>
                    {editMode ? "Done" : "Edit profile"}
                  </button>
                </div>
              </header>

              <div className="profile-dash-glass profile-dash-form-card">
                <h2 className="profile-dash-section-title">Personal information</h2>

                <div className="profile-dash-referral">
                  <span className="profile-dash-referral-label">Your referral code</span>
                  <div className="profile-dash-referral-badge">
                    <code>{referralCode}</code>
                    <button type="button" className="profile-dash-copy-btn" onClick={copyReferralCode} aria-label="Copy referral code">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="profile-dash-toggles">
                  <span className="profile-dash-toggles-title">Notifications</span>
                  <label className="profile-dash-toggle">
                    <input
                      type="checkbox"
                      checked={profile.notificationPrefs?.email !== false}
                      onChange={(e) =>
                        savePreferences({
                          twoFactorEnabled: profile.twoFactorEnabled,
                          notificationPrefs: { ...(profile.notificationPrefs || {}), email: e.target.checked }
                        })
                      }
                    />
                    <span className="profile-dash-toggle-ui" />
                    <span>Email updates</span>
                  </label>
                  <label className="profile-dash-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(profile.notificationPrefs?.sms)}
                      onChange={(e) =>
                        savePreferences({
                          twoFactorEnabled: profile.twoFactorEnabled,
                          notificationPrefs: { ...(profile.notificationPrefs || {}), sms: e.target.checked }
                        })
                      }
                    />
                    <span className="profile-dash-toggle-ui" />
                    <span>SMS alerts</span>
                  </label>
                  <label className="profile-dash-toggle">
                    <input
                      type="checkbox"
                      checked={profile.notificationPrefs?.offers !== false}
                      onChange={(e) =>
                        savePreferences({
                          twoFactorEnabled: profile.twoFactorEnabled,
                          notificationPrefs: { ...(profile.notificationPrefs || {}), offers: e.target.checked }
                        })
                      }
                    />
                    <span className="profile-dash-toggle-ui" />
                    <span>Offers &amp; promotions</span>
                  </label>
                </div>

                <h3 className="profile-dash-subhead">Basic info</h3>
                <div className="profile-dash-grid">
                  <label className="profile-dash-field">
                    <span className="profile-dash-field-label">Full name</span>
                    <div className="profile-dash-input-wrap">
                      <span className="profile-dash-input-icon" aria-hidden>
                        <ProfileNavIcon name="user" />
                      </span>
                      <input value={name} onChange={(e) => setName(e.target.value)} disabled={!editMode} placeholder="Your name" />
                    </div>
                  </label>
                  <label className="profile-dash-field">
                    <span className="profile-dash-field-label">Gender</span>
                    <div className="profile-dash-input-wrap profile-dash-input-wrap--select">
                      <select value={gender} onChange={(e) => setGender(e.target.value)} disabled={!editMode}>
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </label>
                </div>

                <h3 className="profile-dash-subhead">Contact</h3>
                <div className="profile-dash-grid">
                  <label className="profile-dash-field profile-dash-field--full">
                    <span className="profile-dash-field-label">Email</span>
                    <div className="profile-dash-input-wrap">
                      <span className="profile-dash-input-icon" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                      <input value={profile.email} disabled placeholder="email" />
                    </div>
                  </label>
                  <label className="profile-dash-field">
                    <span className="profile-dash-field-label">Phone</span>
                    <div className="profile-dash-input-wrap">
                      <span className="profile-dash-input-icon" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </span>
                      <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!editMode} placeholder="Phone number" inputMode="tel" />
                    </div>
                  </label>
                </div>

                <h3 className="profile-dash-subhead">Personal details</h3>
                <div className="profile-dash-grid">
                  <label className="profile-dash-field">
                    <span className="profile-dash-field-label">Date of birth</span>
                    <div className="profile-dash-input-wrap">
                      <span className="profile-dash-input-icon" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </span>
                      <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} disabled={!editMode} />
                    </div>
                  </label>
                  <label className="profile-dash-field">
                    <span className="profile-dash-field-label">Time zone</span>
                    <div className="profile-dash-input-wrap profile-dash-input-wrap--select">
                      <select disabled>
                        <option>(GMT+5:30) Kolkata</option>
                      </select>
                    </div>
                  </label>
                  <label className="profile-dash-field profile-dash-field--full">
                    <span className="profile-dash-field-label">Profile picture URL</span>
                    <div className="profile-dash-input-wrap">
                      <span className="profile-dash-input-icon" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4M12 8h.01" />
                        </svg>
                      </span>
                      <input value={profilePicture} onChange={(e) => setProfilePicture(e.target.value)} disabled={!editMode} placeholder="https://…" />
                    </div>
                  </label>
                </div>
              </div>

              {message && <p className="profile-dash-inline-msg">{message}</p>}

              {editMode && (
                <div className="profile-dash-sticky-save">
                  <button type="button" className="profile-dash-sticky-save-btn" onClick={saveProfile}>
                    Save changes
                  </button>
                </div>
              )}
            </>
        ) : activeTab === "orders" ? (
          <>
            <h3>My Orders</h3>
            <div className="address-list">
              {myOrders.map((order) => (
                <div key={order._id} className="address-card">
                  <strong>Order #{String(order._id).slice(-8)}</strong>
                  <p>Status: {order.status}</p>
                  <p>Payment: {order.paymentMethod} / {order.paymentStatus || "Pending"}</p>
                  <p>Total: ₹ {order.totalAmount}</p>
                  <div className="address-actions">
                    <button type="button" onClick={() => navigate(`/orders/success/${order._id}`)}>
                      Track Order
                    </button>
                    <button type="button" onClick={() => downloadInvoice(order._id)}>
                      Invoice
                    </button>
                    {["Pending", "Placed", "Order Confirmed"].includes(order.status) && (
                      <button type="button" className="btn-danger" onClick={() => cancelOrder(order._id)}>
                        Cancel Order
                      </button>
                    )}
                    {!order.returnRequest?.requested && order.status === "Delivered" && (
                      <button type="button" className="btn-secondary" onClick={() => requestReturn(order._id)}>
                        Request Return
                      </button>
                    )}
                  </div>
                  {order.returnRequest?.requested && (
                    <>
                      <small>Return Status: {order.returnRequest.status}</small>
                      {order.returnRequest.status === "Requested" && (
                        <div className="address-actions" style={{ marginTop: 8 }}>
                          <button type="button" className="btn-secondary" onClick={() => cancelReturnRequest(order._id)}>
                            Cancel Return Request
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {!myOrders.length && <p>No orders yet.</p>}
            </div>
          </>
        ) : activeTab === "address" ? (
          <>
            <h3>Address Book</h3>
            <form className="address-form" onSubmit={addAddress}>
              <input name="fullName" value={addressForm.fullName} onChange={onAddressChange} placeholder="Full Name" required />
              <input name="phone" value={addressForm.phone} onChange={onAddressChange} placeholder="Phone Number" required />
              <input name="line1" value={addressForm.line1} onChange={onAddressChange} placeholder="Address Line 1" required />
              <input name="line2" value={addressForm.line2} onChange={onAddressChange} placeholder="Address Line 2 (Optional)" />
              <div className="two-col">
                <input name="city" value={addressForm.city} onChange={onAddressChange} placeholder="City" required />
                <input name="state" value={addressForm.state} onChange={onAddressChange} placeholder="State" required />
              </div>
              <input name="pincode" value={addressForm.pincode} onChange={onAddressChange} placeholder="Pincode" required />
              <button type="submit">Add Shipping Address</button>
            </form>
            <div className="address-list">
              {profile.addresses?.map((address) => (
                <div key={address._id} className="address-card">
                  <strong>{address.fullName}</strong>
                  <p>
                    {address.line1}, {address.line2 ? `${address.line2}, ` : ""}
                    {address.city}, {address.state} - {address.pincode}
                  </p>
                  <p>Phone: {address.phone}</p>
                  <div className="address-actions">
                    {!address.isDefault && (
                      <button type="button" onClick={() => setDefaultAddress(address._id)}>
                        Set Default
                      </button>
                    )}
                    {address.isDefault && <span className="default-badge">Default</span>}
                    <button type="button" className="btn-danger" onClick={() => deleteAddress(address._id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : activeTab === "payments" ? (
          <>
            {/* <h3>Payment Methods</h3>
            <form className="address-form" onSubmit={addPaymentMethod}>
              <select value={paymentForm.type} onChange={(e) => setPaymentForm((prev) => ({ ...prev, type: e.target.value }))}>
                <option value="CARD">Card</option>
                <option value="UPI">UPI</option>
                <option value="WALLET">Wallet</option>
              </select>
              <input
                value={paymentForm.label}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Card/UPI label"
                required
              />
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={paymentForm.isDefault}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
                />
                <span>Set as default payment method</span>
              </label>
              <button type="submit">Save Payment Method</button>
            </form>
            <div className="address-list">
              {(profile.paymentMethods || []).map((method) => (
                <div key={method.id} className="address-card">
                  <strong>{method.type}</strong>
                  <p>{method.label}</p>
                  <div className="address-actions">
                    {!method.isDefault && (
                      <button type="button" onClick={() => setDefaultPaymentMethod(method.id)}>
                        Set Default
                      </button>
                    )}
                    {method.isDefault && <span className="default-badge">Default</span>}
                    <button type="button" className="btn-danger" onClick={() => removePaymentMethod(method.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {!profile.paymentMethods?.length && <p>No payment methods saved yet.</p>}
            </div> */}
          </>
        ) : activeTab === "wishlist" ? (
          <>
            <h3>Wishlist</h3>
            <p>Saved products: {wishlist.length}</p>
            <button type="button" onClick={() => navigate("/wishlist")}>
              Open Full Wishlist
            </button>
          </>
        ) : activeTab === "cart" ? (
          <>
            <h3>Saved Cart</h3>
            <p>Local cart items: {readPersistedCart().length}</p>
            <p>Cloud synced cart items: {cartSyncItems.length}</p>
            <button type="button" onClick={syncCurrentCart}>
              Sync Current Cart Across Devices
            </button>
          </>
        ) : activeTab === "security" ? (
          <>
            <h3>Security Settings</h3>
            <form className="address-form" onSubmit={changeEmailAction}>
              <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New Email" type="email" required />
              <input
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                placeholder="Current Password for verification"
                type="password"
                required
              />
              <button type="submit">Change Email</button>
            </form>
            <label className="checkbox-row security-2fa-row">
              <input
                type="checkbox"
                checked={Boolean(profile.twoFactorEnabled)}
                onChange={(e) => savePreferences({ twoFactorEnabled: e.target.checked, notificationPrefs: profile.notificationPrefs })}
              />
              <span>Enable 2FA — email OTP required on each login</span>
            </label>
            <p className="security-2fa-note">When enabled, after your password we email a one-time code.</p>
            <button type="button" className="btn-danger" onClick={logoutAllDevices}>
              Logout From All Devices
            </button>
          </>
        ) : activeTab === "coupons" ? (
          <>
            <h3>Coupons & Offers</h3>
            <div className="address-list">
              {availableCoupons.map((coupon) => (
                <div key={coupon._id} className="address-card">
                  <strong>{coupon.code}</strong>
                  <p>
                    {coupon.discountType} {coupon.discountValue} | Exp: {new Date(coupon.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
              {!availableCoupons.length && <p>No active coupons right now.</p>}
            </div>
          </>
        ) : activeTab === "notifications" ? (
          <>
            <h3>Notifications Preferences</h3>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={profile.notificationPrefs?.email !== false}
                onChange={(e) =>
                  savePreferences({
                    twoFactorEnabled: profile.twoFactorEnabled,
                    notificationPrefs: { ...(profile.notificationPrefs || {}), email: e.target.checked }
                  })
                }
              />
              <span>Email updates</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={Boolean(profile.notificationPrefs?.sms)}
                onChange={(e) =>
                  savePreferences({
                    twoFactorEnabled: profile.twoFactorEnabled,
                    notificationPrefs: { ...(profile.notificationPrefs || {}), sms: e.target.checked }
                  })
                }
              />
              <span>SMS alerts</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={profile.notificationPrefs?.offers !== false}
                onChange={(e) =>
                  savePreferences({
                    twoFactorEnabled: profile.twoFactorEnabled,
                    notificationPrefs: { ...(profile.notificationPrefs || {}), offers: e.target.checked }
                  })
                }
              />
              <span>Offers & promotions</span>
            </label>
          </>
        ) : activeTab === "insights" ? (
          <>
            <h3>Order Insights</h3>
            <p>Total Spent: ₹ {Number(insights.totalSpent || 0).toFixed(2)}</p>
            <h4>Favorite Categories/Products</h4>
            {(insights.favoriteCategories || []).map((item) => (
              <p key={item.name}>
                {item.name}: {item.quantity}
              </p>
            ))}
            <h4>Purchase History Graph Data</h4>
            {(insights.purchaseHistory || []).map((item) => (
              <p key={item.month}>
                {item.month}: ₹ {item.amount}
              </p>
            ))}
          </>
        ) : activeTab === "recommendations" ? (
          <>
            <h3>Recommendations</h3>
            <div className="address-list">
              {recommendations.map((item) => (
                <div key={item._id} className="address-card">
                  <strong>{item.name}</strong>
                  <p>
                    {item.category} / {item.subCategory}
                  </p>
                  <p>₹ {item.price}</p>
                </div>
              ))}
              {!recommendations.length && <p>No recommendations yet.</p>}
            </div>
          </>
        ) : activeTab === "reviews" ? (
          <>
            <h3>Reviews & Ratings</h3>
            <div className="address-list">
              {myReviews.map((review) => (
                <div key={review._id} className="address-card">
                  <strong>{review.productId?.name || "Product"}</strong>
                  <p>Rating: {review.rating}/5</p>
                  <p>{review.comment || "-"}</p>
                  <div className="address-actions">
                    <button type="button" className="btn-danger" onClick={() => deleteReview(review._id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!myReviews.length && <p>No reviews yet.</p>}
            </div>
          </>
        ) : (
          <>
            <h3>Change Password</h3>
            <form className="address-form" onSubmit={submitPasswordChange}>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current Password"
                required
              />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                required
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm New Password"
                required
              />
              <button type="submit">Update Password</button>
            </form>
          </>
        )}
        {activeTab !== "account" && message && <p className="profile-dash-inline-msg">{message}</p>}
      </section>
      </div>
    </div>
  );
};

const WishlistPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("wishlist") || "[]"));
  const [cart, setCart] = useState(() => readPersistedCart());

  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    writePersistedCart(cart);
  }, [cart]);

  const removeWishlist = (id) => setWishlist((prev) => prev.filter((item) => item._id !== id));

  const addToCart = (item) => {
    if (!token) {
      navigate("/auth");
      return;
    }
    setCart((prev) => {
      const hit = prev.find((p) => p.productId === item._id);
      if (hit) return prev.map((p) => (p.productId === item._id ? { ...p, quantity: p.quantity + 1 } : p));
      return [...prev, { productId: item._id, name: item.name, price: item.price, quantity: 1, image: item.image || "" }];
    });
    navigate("/checkout");
  };

  return (
    <div className="card">
      <h2>My Wishlist</h2>
      <div className="grid">
        {wishlist.map((p) => (
          <div key={p._id} className="card product-card premium-card">
            <button type="button" className="wishlist-btn active" onClick={() => removeWishlist(p._id)}>
              ❤
            </button>
            <div className="product-image">{p.image ? <img src={p.image} alt={p.name} /> : <div className="image-fallback">{p.name}</div>}</div>
            <div className="product-content">
              <h3 className="product-title">{p.name}</h3>
              <p className="product-price">₹ {p.price}</p>
              <button className="add-btn" onClick={() => addToCart(p)}>
                Add to Cart
              </button>
            </div>
          </div>
        ))}
        {!wishlist.length && <p>No items in wishlist yet.</p>}
      </div>
    </div>
  );
};

const emptyProductForm = {
  name: "",
  price: "",
  compareAtPrice: "",
  description: "",
  category: "",
  subCategory: "",
  image: "",
  imagesText: "",
  stock: ""
};

const CATEGORY_MAP = {
  "Packing material": [
    "3 Ply Box",
    "3 Ply White Box",
    "Flap Box",
    "White Flap Box",
    "Corrugated & Kraft Paper Roll",
    "Paper Bubble Wrap & Protection",
    "Polybag"
  ],
  "Grocery items": ["Rice", "Pulses", "Dry Fruits", "Spices", "Tea & Coffee", "Edible Oils"],
  "Custom gift items": [],
  "Beauty items": ["Face Wash", "Serum", "Lip Balm", "Body Lotion", "Hair Oil", "Sunscreen"],
  "Wooden items": [
    "Coasters",
    "Fridge Magnets",
    "Wall Hanging",
    "Wooden File Tray",
    "Wooden Pen & Pencil Stand",
    "Engraved Night Lamp",
    "Wooden Table"
  ]
};
const DEFAULT_CATEGORY_MENUS = Object.entries(CATEGORY_MAP).map(([title, items]) => ({ title, items }));

const AdminProducts = ({ mode = "add-product", listFilter = "", onNavigateAdd }) => {
  const [products, setProducts] = useState([]);
  const [categoryMedia, setCategoryMedia] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORY_MENUS);
  const [form, setForm] = useState(emptyProductForm);
  const [packOptionRows, setPackOptionRows] = useState([{ label: "", price: "" }]);
  const [editingId, setEditingId] = useState("");
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [primaryDrag, setPrimaryDrag] = useState(false);
  const [galleryDrag, setGalleryDrag] = useState(false);
  const [mediaForm, setMediaForm] = useState({ category: "", subCategory: "", image: "" });
  const [mediaUploading, setMediaUploading] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    category: "",
    bannerTarget: "main",
    subCategory: "",
    image: "",
    editingId: null
  });
  const [bannerUploading, setBannerUploading] = useState(false);

  const loadProducts = async () => {
    const { data } = await API.get("/products");
    setProducts(data);
  };
  const loadCategoryMedia = async () => {
    const { data } = await API.get("/category-media");
    setCategoryMedia(data);
  };
  const loadCategories = async () => {
    const { data } = await API.get("/categories");
    setCategories((data || []).map((item) => ({ title: item.title, items: item.items || [] })));
  };

  useEffect(() => {
    loadProducts();
    loadCategoryMedia();
    loadCategories();
  }, []);

  const filteredProducts = useMemo(() => {
    const q = String(listFilter || "").trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        String(p.name || "").toLowerCase().includes(q) ||
        String(p.category || "").toLowerCase().includes(q) ||
        String(p.subCategory || "").toLowerCase().includes(q)
    );
  }, [products, listFilter]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "category") {
        return { ...prev, category: value, subCategory: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const resetForm = () => {
    setForm(emptyProductForm);
    setPackOptionRows([{ label: "", price: "" }]);
    setEditingId("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const allowedSubCategories = categories.find((item) => item.title === form.category)?.items || [];
    if (!allowedSubCategories.includes(form.subCategory)) {
      setMessage("Please choose a valid category and sub-category.");
      return;
    }
    const extraImages = form.imagesText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const packOptions = packOptionRows
      .filter((r) => r.label.trim() !== "" && r.price !== "" && !Number.isNaN(Number(r.price)))
      .map((r) => ({ label: r.label.trim(), price: Number(r.price) }));
    const payload = {
      name: form.name,
      price: Number(form.price),
      compareAtPrice: form.compareAtPrice === "" ? null : Number(form.compareAtPrice),
      description: form.description,
      category: form.category,
      subCategory: form.subCategory,
      image: form.image,
      images: extraImages,
      packOptions,
      stock: Number(form.stock)
    };
    if (editingId) {
      await API.put(`/products/${editingId}`, payload);
      setMessage("Product updated successfully");
    } else {
      await API.post("/products", payload);
      setMessage("Product added successfully");
    }
    resetForm();
    loadProducts();
  };

  const onEdit = (product) => {
    setEditingId(product._id);
    const extras = Array.isArray(product.images) ? product.images : [];
    setForm({
      name: product.name || "",
      price: String(product.price ?? ""),
      compareAtPrice: product.compareAtPrice != null && product.compareAtPrice !== "" ? String(product.compareAtPrice) : "",
      description: product.description || "",
      category: product.category || "",
      subCategory: product.subCategory || "",
      image: product.image || "",
      imagesText: extras.join("\n"),
      stock: String(product.stock ?? "")
    });
    const packs = Array.isArray(product.packOptions) ? product.packOptions : [];
    setPackOptionRows(
      packs.length ? packs.map((p) => ({ label: p.label || "", price: String(p.price ?? "") })) : [{ label: "", price: "" }]
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    await API.delete(`/products/${id}`);
    setMessage("Product deleted");
    if (editingId === id) resetForm();
    loadProducts();
  };

  const uploadPrimaryFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setMessage("");
    try {
      const data = new FormData();
      data.append("image", file);
      const res = await API.post("/products/upload", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setForm((prev) => ({ ...prev, image: res.data.imageUrl }));
      setMessage("Primary image uploaded");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    await uploadPrimaryFile(file);
  };

  const uploadGalleryFile = async (file, inputEl) => {
    if (!file) return;
    setUploading(true);
    setMessage("");
    try {
      const data = new FormData();
      data.append("image", file);
      const res = await API.post("/products/upload", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setForm((prev) => ({
        ...prev,
        imagesText: prev.imagesText ? `${prev.imagesText.trim()}\n${res.data.imageUrl}` : res.data.imageUrl
      }));
      setMessage("Gallery image URL appended");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Gallery upload failed");
    } finally {
      setUploading(false);
      if (inputEl) inputEl.value = "";
    }
  };

  const handleGalleryImageUpload = async (e) => {
    await uploadGalleryFile(e.target.files?.[0], e.target);
  };

  const onPrimaryDrop = (e) => {
    e.preventDefault();
    setPrimaryDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.type?.startsWith("image/")) uploadPrimaryFile(file);
  };

  const onGalleryDrop = (e) => {
    e.preventDefault();
    setGalleryDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.type?.startsWith("image/")) uploadGalleryFile(file, null);
  };

  const addPackRow = () => setPackOptionRows((rows) => [...rows, { label: "", price: "" }]);
  const removePackRow = (idx) =>
    setPackOptionRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)));
  const setPackRow = (idx, field, value) =>
    setPackOptionRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const subCategoryOptions = categories.find((item) => item.title === form.category)?.items || [];
  const mediaSubCategoryOptions = categories.find((item) => item.title === mediaForm.category)?.items || [];
  const bannerSubCategoryOptions = categories.find((item) => item.title === bannerForm.category)?.items || [];
  const savedHorizontalBanners = categoryMedia
    .filter((item) => isHorizontalBannerRecord(item.subCategory))
    .sort((a, b) => {
      const c = String(a.category).localeCompare(String(b.category));
      if (c !== 0) return c;
      return String(a.subCategory).localeCompare(String(b.subCategory));
    });
  const savedSubCategoryImages = categoryMedia.filter((item) => !isHorizontalBannerRecord(item.subCategory));

  const onMediaChange = (e) => {
    const { name, value } = e.target;
    setMediaForm((prev) => {
      if (name === "category") return { ...prev, category: value, subCategory: "" };
      return { ...prev, [name]: value };
    });
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaUploading(true);
    try {
      const data = new FormData();
      data.append("image", file);
      const res = await API.post("/category-media/upload", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMediaForm((prev) => ({ ...prev, image: res.data.imageUrl }));
      setMessage("Category image uploaded");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Category image upload failed");
    } finally {
      setMediaUploading(false);
    }
  };

  const saveCategoryMedia = async (e) => {
    e.preventDefault();
    if (!mediaForm.category || !mediaForm.image) {
      setMessage("Choose category and upload image first");
      return;
    }
    await API.post("/category-media", mediaForm);
    setMessage("Category image saved");
    setMediaForm({ category: "", subCategory: "", image: "" });
    loadCategoryMedia();
  };

  const handleHorizontalBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const data = new FormData();
      data.append("image", file);
      const res = await API.post("/category-media/upload", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBannerForm((prev) => ({ ...prev, image: res.data.imageUrl }));
      setMessage("Horizontal category banner uploaded");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Banner upload failed");
    } finally {
      setBannerUploading(false);
    }
  };

  const saveHorizontalBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.category || !bannerForm.image) {
      setMessage("Choose category and image URL first");
      return;
    }
    if (bannerForm.bannerTarget === "sub") {
      if (!bannerForm.subCategory) {
        setMessage("Select a subcategory for this banner (or choose whole category).");
        return;
      }
    }
    const subCategory =
      bannerForm.bannerTarget === "main" ? HORIZONTAL_BANNER_MAIN : horizontalBannerSubKey(bannerForm.subCategory);
    await API.post("/category-media", { category: bannerForm.category, subCategory, image: bannerForm.image });
    setMessage(bannerForm.editingId ? "Horizontal banner updated" : "Horizontal banner saved");
    setBannerForm({ category: "", bannerTarget: "main", subCategory: "", image: "", editingId: null });
    loadCategoryMedia();
  };

  const startEditHorizontalBanner = (item) => {
    const parsed = parseHorizontalBannerTarget(item.subCategory);
    if (!parsed) return;
    setBannerForm({
      category: item.category,
      bannerTarget: parsed.type === "main" ? "main" : "sub",
      subCategory: parsed.type === "sub" ? parsed.subName : "",
      image: item.image || "",
      editingId: item._id
    });
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelHorizontalBannerEdit = () => {
    setBannerForm({ category: "", bannerTarget: "main", subCategory: "", image: "", editingId: null });
    setMessage("");
  };

  const deleteHorizontalBanner = async (id) => {
    if (!window.confirm("Delete this horizontal banner?")) return;
    try {
      await API.delete(`/category-media/${id}`);
      setMessage("Banner deleted");
      if (bannerForm.editingId === id) cancelHorizontalBannerEdit();
      loadCategoryMedia();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Delete failed");
    }
  };

  // Allow editing from the Products tab by revealing the form when an item is being edited.
  const showAddForms = mode === "add-product" || Boolean(editingId);
  const showManageProducts = mode === "products";

  return (
    <div className="admin-layout">
      {showAddForms && (
        <>
          <form onSubmit={onSubmit} className="card admin-form">
            <h2>{editingId ? "Update Product" : "Add New Product"}</h2>
            <input name="name" value={form.name} onChange={onChange} placeholder="Product name" required />
            <label className="field-label">Selling price (base — used when no pack options)</label>
            <input name="price" value={form.price} onChange={onChange} placeholder="Price" type="number" min="0" required />
            <label className="field-label">Compare-at / MRP (optional, shown struck-through)</label>
            <input
              name="compareAtPrice"
              value={form.compareAtPrice}
              onChange={onChange}
              placeholder="e.g. 10476 — leave empty to auto-calculate from sale price"
              type="number"
              min="0"
            />
            <select name="category" value={form.category} onChange={onChange} required>
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.title} value={category.title}>
                  {category.title}
                </option>
              ))}
            </select>
            <select name="subCategory" value={form.subCategory} onChange={onChange} required disabled={!form.category}>
              <option value="">Select sub-category</option>
              {subCategoryOptions.map((subCategory) => (
                <option key={subCategory} value={subCategory}>
                  {subCategory}
                </option>
              ))}
            </select>
            <input name="stock" value={form.stock} onChange={onChange} placeholder="Stock quantity" type="number" min="0" required />
            <label className="field-label">Primary image (listing / first gallery slide)</label>
            <div
              className={`admin-premium-dropzone${primaryDrag ? " drag" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setPrimaryDrag(true);
              }}
              onDragLeave={() => setPrimaryDrag(false)}
              onDrop={onPrimaryDrop}
              onClick={() => document.getElementById("admin-primary-file")?.click()}
              role="presentation"
            >
              <p>
                <strong>Drag & drop</strong> an image here, or click to browse
              </p>
              <input id="admin-primary-file" type="file" accept="image/*" className="sr-only" onChange={handleImageUpload} />
            </div>
            {uploading && <small>Uploading image...</small>}
            <input name="image" value={form.image} onChange={onChange} placeholder="Image URL (auto-filled after upload)" required />
            {form.image && (
              <div className="admin-image-preview">
                <img src={form.image} alt="Preview" />
              </div>
            )}
            <label className="field-label">More gallery images</label>
            <small>Upload files to append URLs, or paste one image URL per line.</small>
            <div
              className={`admin-premium-dropzone${galleryDrag ? " drag" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setGalleryDrag(true);
              }}
              onDragLeave={() => setGalleryDrag(false)}
              onDrop={onGalleryDrop}
              onClick={() => document.getElementById("admin-gallery-file")?.click()}
              role="presentation"
            >
              <p>
                <strong>Drag & drop</strong> gallery images, or click to upload
              </p>
              <input id="admin-gallery-file" type="file" accept="image/*" className="sr-only" onChange={handleGalleryImageUpload} />
            </div>
            <textarea name="imagesText" value={form.imagesText} onChange={onChange} placeholder="Extra image URLs, one per line" rows={3} />
            <label className="field-label">Pack options (optional)</label>
            <small>If you add rows, the product page shows packs and uses each row&apos;s price (ignores base price until a pack is chosen).</small>
            {packOptionRows.map((row, idx) => (
              <div key={`pack-${idx}`} className="pack-option-row" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <input
                  style={{ flex: "1 1 180px" }}
                  placeholder="e.g. Pack of 1000"
                  value={row.label}
                  onChange={(e) => setPackRow(idx, "label", e.target.value)}
                />
                <input
                  style={{ flex: "0 0 120px" }}
                  type="number"
                  min="0"
                  placeholder="Price ₹"
                  value={row.price}
                  onChange={(e) => setPackRow(idx, "price", e.target.value)}
                />
                <button type="button" className="btn-secondary" onClick={() => removePackRow(idx)} disabled={packOptionRows.length <= 1}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="btn-secondary" onClick={addPackRow}>
              Add pack option
            </button>
            <label className="field-label">Description (Markdown)</label>
            <small className="admin-hint" style={{ display: "block", marginBottom: 8 }}>
              Markdown: <code>##</code> main title, optional spec <code>|</code> table, then <code>### Section title</code> for each glass card (e.g. two <code>###</code> blocks = two side‑by‑side cards on desktop).
              Use <code>**bold**</code> for cyan highlights. Typos like <code>##Heading</code> or <code>-Item</code> are auto-fixed.
            </small>
            <div className="admin-desc-ai-grid">
              <div className="admin-desc-column">
                <textarea
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  placeholder={`## Key Features & Benefits\n\n### Strong & Durable\n- First bullet with **bold words**\n- Second bullet\n\n| Customization | Available for bulk orders |\n| --- | --- |\n| Shipping | Free Shipping |`}
                  rows={14}
                />
              </div>
              <AIProductDescriptionGenerator
                compact
                categories={categories.map((c) => c.title)}
                prefillName={form.name}
                prefillCategory={form.category}
                onApplyDescription={(text) => setForm((prev) => ({ ...prev, description: text }))}
              />
            </div>
            <div className="admin-form-actions">
              <button type="submit">{editingId ? "Update Product" : "Add Product"}</button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </div>
            {message && <small>{message}</small>}
          </form>

          <form onSubmit={saveCategoryMedia} className="card admin-form">
            <h2>Category Image Manager</h2>
            <select name="category" value={mediaForm.category} onChange={onMediaChange} required>
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.title} value={category.title}>
                  {category.title}
                </option>
              ))}
            </select>
            <select name="subCategory" value={mediaForm.subCategory} onChange={onMediaChange} disabled={!mediaForm.category}>
              <option value="">Main category image</option>
              {mediaSubCategoryOptions.map((subCategory) => (
                <option key={subCategory} value={subCategory}>
                  {subCategory}
                </option>
              ))}
            </select>
            <input type="file" accept="image/*" onChange={handleCategoryImageUpload} />
            {mediaUploading && <small>Uploading category image...</small>}
            <input name="image" value={mediaForm.image} onChange={onMediaChange} placeholder="Image URL" required />
            {mediaForm.image && (
              <div className="admin-image-preview">
                <img src={mediaForm.image} alt="Category preview" />
              </div>
            )}
            <button type="submit">Save Category Image</button>
          </form>

          <form onSubmit={saveHorizontalBanner} className="card admin-form">
            <h2>Horizontal Category Banner Manager</h2>
            <small>
              Wide banner on the category page. Choose <strong>whole category</strong> or a <strong>subcategory</strong> (when a shopper
              filters by that sub, its banner shows; otherwise the category banner shows).
            </small>
            <label className="field-label">Category</label>
            <select
              name="category"
              value={bannerForm.category}
              onChange={(e) =>
                setBannerForm((prev) => ({
                  ...prev,
                  category: e.target.value,
                  subCategory: prev.bannerTarget === "sub" ? "" : prev.subCategory
                }))
              }
              required
              disabled={Boolean(bannerForm.editingId)}
            >
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.title} value={category.title}>
                  {category.title}
                </option>
              ))}
            </select>
            <label className="field-label">Applies to</label>
            <select
              value={bannerForm.bannerTarget}
              onChange={(e) =>
                setBannerForm((prev) => ({
                  ...prev,
                  bannerTarget: e.target.value,
                  subCategory: e.target.value === "main" ? "" : prev.subCategory
                }))
              }
              disabled={Boolean(bannerForm.editingId)}
            >
              <option value="main">Whole category (default)</option>
              <option value="sub">Specific subcategory</option>
            </select>
            {bannerForm.bannerTarget === "sub" && (
              <>
                <label className="field-label">Subcategory</label>
                <select
                  value={bannerForm.subCategory}
                  onChange={(e) => setBannerForm((prev) => ({ ...prev, subCategory: e.target.value }))}
                  required
                  disabled={Boolean(bannerForm.editingId) || !bannerForm.category}
                >
                  <option value="">Select subcategory</option>
                  {bannerSubCategoryOptions.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
                {!bannerSubCategoryOptions.length && bannerForm.category ? (
                  <small>This category has no subcategories — use &quot;Whole category&quot; only.</small>
                ) : null}
              </>
            )}
            <input type="file" accept="image/*" onChange={handleHorizontalBannerUpload} />
            {bannerUploading && <small>Uploading banner image...</small>}
            <label className="field-label">Image URL</label>
            <input
              name="image"
              value={bannerForm.image}
              onChange={(e) => setBannerForm((prev) => ({ ...prev, image: e.target.value }))}
              placeholder="Banner image URL"
              required
            />
            {bannerForm.image && (
              <div className="admin-image-preview">
                <img src={bannerForm.image} alt="Horizontal banner preview" />
              </div>
            )}
            <div className="admin-form-actions">
              <button type="submit">{bannerForm.editingId ? "Update horizontal banner" : "Save horizontal banner"}</button>
              {bannerForm.editingId ? (
                <button type="button" className="btn-secondary" onClick={cancelHorizontalBannerEdit}>
                  Cancel edit
                </button>
              ) : null}
            </div>
          </form>
        </>
      )}

      {showManageProducts && (
        <>
          <div className="card">
            <div className="admin-premium-products-toolbar">
              <h2>Products</h2>
              {typeof onNavigateAdd === "function" ? (
                <button type="button" className="admin-premium-btn-gradient" onClick={onNavigateAdd}>
                  + New product
                </button>
              ) : null}
            </div>
            <div className="admin-premium-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => (
                    <tr key={p._id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="admin-premium-thumb">
                            {p.image ? <img src={p.image} alt="" /> : <span>{String(p.name || "").slice(0, 2).toUpperCase()}</span>}
                          </div>
                          <strong>{p.name}</strong>
                        </div>
                      </td>
                      <td>
                        {p.category} / {p.subCategory}
                      </td>
                      <td>₹ {p.price}</td>
                      <td>{p.stock}</td>
                      <td>
                        <span
                          className={`admin-premium-status${Number(p.stock) <= 0 ? " admin-premium-status--bad" : Number(p.stock) < 10 ? " admin-premium-status--warn" : " admin-premium-status--ok"}`}
                        >
                          {Number(p.stock) <= 0 ? "Out of stock" : Number(p.stock) < 10 ? "Low stock" : "Active"}
                        </span>
                      </td>
                      <td>
                        <div className="admin-premium-row-actions">
                          <button type="button" className="admin-premium-icon-action" title="Edit" onClick={() => onEdit(p)}>
                            ✎
                          </button>
                          <button type="button" className="admin-premium-icon-action danger" title="Delete" onClick={() => onDelete(p._id)}>
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!products.length ? (
                <p style={{ padding: 16, color: "#94a3b8" }}>No products yet. Add your first product.</p>
              ) : !filteredProducts.length ? (
                <p style={{ padding: 16, color: "#94a3b8" }}>No products match your search.</p>
              ) : null}
            </div>
          </div>
          <div className="card">
            <h2>Saved Horizontal Category Banners</h2>
            <div className="admin-product-list">
              {savedHorizontalBanners.map((item) => {
                const parsed = parseHorizontalBannerTarget(item.subCategory);
                const scopeLabel =
                  parsed?.type === "sub" ? `Subcategory: ${parsed.subName}` : "Whole category (default)";
                return (
                  <div key={item._id} className="admin-product-item admin-horizontal-banner-saved-row">
                    <div>
                      <strong>{item.category}</strong>
                      <p>{scopeLabel}</p>
                    </div>
                    <div className="admin-horizontal-banner-end">
                      <div className="admin-image-preview">
                        <img src={item.image} alt="" />
                      </div>
                      <div className="admin-item-actions">
                        <button type="button" onClick={() => startEditHorizontalBanner(item)}>
                          Edit
                        </button>
                        <button type="button" className="btn-danger" onClick={() => deleteHorizontalBanner(item._id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!savedHorizontalBanners.length && <p>No horizontal banners saved yet.</p>}
            </div>
          </div>
          <div className="card">
            <h2>Saved Category Images</h2>
            <div className="admin-product-list">
              {savedSubCategoryImages.map((item) => (
                <div key={`${item.category}-${item.subCategory}`} className="admin-product-item">
                  <div>
                    <strong>{item.category}</strong>
                    <p>{item.subCategory || "Main category image"}</p>
                  </div>
                  <div className="admin-image-preview">
                    <img src={item.image} alt={`${item.category} ${item.subCategory}`} />
                  </div>
                </div>
              ))}
              {!savedSubCategoryImages.length && <p>No sub-category images saved yet.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("deliver")) return "admin-premium-status admin-premium-status--ok";
  if (s.includes("cancel")) return "admin-premium-status admin-premium-status--bad";
  if (s.includes("ship") || s.includes("out for")) return "admin-premium-status admin-premium-status--ship";
  if (s.includes("pending") || s.includes("placed")) return "admin-premium-status admin-premium-status--pend";
  return "admin-premium-status admin-premium-status--warn";
}

function AdminOrderTimeline({ status }) {
  const labels = ["Pending", "Shipped", "Delivered"];
  let idx = 0;
  const s = String(status || "");
  if (s === "Delivered") idx = 2;
  else if (s.includes("Shipped") || s === "Out For Delivery") idx = 1;
  return (
    <div className="admin-premium-order-timeline" aria-label="Order progress">
      {labels.map((label, i) => (
        <Fragment key={label}>
          <span className={`admin-premium-timeline-step${i < idx ? " done" : i === idx ? " current" : ""}`}>
            <span className="admin-premium-timeline-dot" />
            {label}
          </span>
          {i < labels.length - 1 ? <span className="admin-premium-timeline-line" /> : null}
        </Fragment>
      ))}
    </div>
  );
}

function AdminAnalyticsCharts({ analytics, orders }) {
  const months = analytics?.ordersByMonth || [];
  const maxM = Math.max(1, ...months.map((m) => Number(m.ordersCount) || 0));
  const pts = months
    .map((m, i) => {
      const x = months.length <= 1 ? 50 : 8 + (i / Math.max(1, months.length - 1)) * 84;
      const y = 90 - ((Number(m.ordersCount) || 0) / maxM) * 75;
      return `${x},${y}`;
    })
    .join(" ");

  const top = (analytics?.topProducts || []).slice(0, 5);
  const maxT = Math.max(1, ...top.map((t) => Number(t.quantity) || 0));

  const buckets = (orders || []).reduce((acc, o) => {
    const raw = String(o.status || "Other");
    const key = raw.length > 14 ? `${raw.slice(0, 12)}…` : raw;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(buckets).slice(0, 6);
  const tot = entries.reduce((s, [, n]) => s + n, 0) || 1;
  let accPct = 0;
  const cols = ["#6366f1", "#38bdf8", "#22c55e", "#fbbf24", "#f472b6", "#a78bfa"];
  const gradStops = entries
    .map(([label, count], i) => {
      const start = accPct;
      accPct += (count / tot) * 100;
      return `${cols[i % cols.length]} ${start}% ${accPct}%`;
    })
    .join(", ");

  return (
    <div className="admin-premium-charts">
      <div className="glass-panel glass-panel--lift">
        <h4 className="admin-premium-chart-title">Orders by month</h4>
        <svg className="admin-premium-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="admLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
          </defs>
          {months.length ? (
            <>
              <polyline
                fill="none"
                stroke="url(#admLineGrad)"
                strokeWidth="2.2"
                points={pts}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {pts.split(" ").map((pair, i) => {
                const [x, y] = pair.split(",").map(Number);
                return <circle key={`c-${i}`} cx={x} cy={y} r="2.5" fill="#38bdf8" />;
              })}
            </>
          ) : (
            <text x="50" y="54" textAnchor="middle" fill="#64748b" fontSize="8">
              No data yet
            </text>
          )}
        </svg>
      </div>
      <div className="glass-panel glass-panel--lift">
        <h4 className="admin-premium-chart-title">Top products (units)</h4>
        <svg className="admin-premium-chart-svg" viewBox="0 0 100 100">
          {top.length ? (
            top.map((t, i) => {
              const h = ((Number(t.quantity) || 0) / maxT) * 70;
              const x = 8 + i * 18;
              return (
                <g key={t.name || i}>
                  <rect x={x} y={88 - h} width="14" height={h} rx="2" fill={i % 2 === 0 ? "#6366f1" : "#38bdf8"} opacity="0.9" />
                  <text x={x + 7} y="97" textAnchor="middle" fill="#94a3b8" fontSize="5">
                    {String(t.name || "").slice(0, 5)}
                  </text>
                </g>
              );
            })
          ) : (
            <text x="50" y="54" textAnchor="middle" fill="#64748b" fontSize="8">
              No top products
            </text>
          )}
        </svg>
      </div>
      <div className="glass-panel glass-panel--lift">
        <h4 className="admin-premium-chart-title">Order mix</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
          <div style={{ position: "relative", width: 132, height: 132 }}>
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: entries.length ? `conic-gradient(${gradStops})` : "#334155"
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 36,
                borderRadius: "50%",
                background: "#0f172a",
                border: "1px solid rgba(148,163,184,0.15)"
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", maxWidth: 200 }}>
            {entries.map(([label, count], i) => (
              <div key={label} style={{ marginBottom: 6 }}>
                <span style={{ color: cols[i % cols.length] }}>●</span> {label}: {count}
              </div>
            ))}
            {!entries.length && <span>No orders yet</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [banners, setBanners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [returns, setReturns] = useState([]);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [inventoryThreshold, setInventoryThreshold] = useState(10);
  const [inventoryStats, setInventoryStats] = useState(null);
  const [stockDraftById, setStockDraftById] = useState({});
  const [updatingStockId, setUpdatingStockId] = useState("");
  const [reviewsData, setReviewsData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORY_MENUS.map((item, idx) => ({ _id: `fallback-${idx}`, ...item })));
  const [newCategory, setNewCategory] = useState("");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [updatingOrderId, setUpdatingOrderId] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [pricingForm, setPricingForm] = useState(() => getPricingConfig());
  const [couponForm, setCouponForm] = useState({
    code: "",
    discountType: "PERCENT",
    discountValue: "",
    minOrderAmount: "",
    maxDiscountAmount: "",
    expiresAt: ""
  });
  const [bannerForm, setBannerForm] = useState({ image: "" });
  const [bannerAnnouncementForm, setBannerAnnouncementForm] = useState("");
  const [notifyForm, setNotifyForm] = useState({
    target: "",
    subject: "",
    message: ""
  });
  const [adminSearch, setAdminSearch] = useState("");
  const [productsSubview, setProductsSubview] = useState("list");
  const [settingsSection, setSettingsSection] = useState("store");
  const [profileOpen, setProfileOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isSuperAdmin = user?.role === "super_admin";

  const orderStatuses = ["Pending", "Placed", "Order Confirmed", "Shipped", "Out For Delivery", "Delivered", "Cancelled"];
  const returnStatuses = ["Requested", "Approved", "Rejected", "Refunded"];
  const roleOptions = ["customer", "sub_admin", "super_admin"];

  const computedPaymentStatus = (order) => {
    if (order.paymentStatus) return order.paymentStatus;
    if (order.paymentMethod === "Cash On Delivery") return order.status === "Delivered" ? "Paid" : "Pending";
    return order.paymentId ? "Paid" : "Pending";
  };

  const orderLocationLink = (order) => {
    const addr = order?.deliveryAddress || {};
    if (addr.latitude && addr.longitude) {
      return `https://maps.google.com/?q=${addr.latitude},${addr.longitude}`;
    }
    const query = [addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
    if (!query) return "";
    return `https://maps.google.com/?q=${encodeURIComponent(query)}`;
  };

  const loadInventoryAlerts = async (limitParam) => {
    const lim = Math.min(500, Math.max(0, Number(limitParam ?? inventoryThreshold ?? 10)));
    try {
      const { data } = await API.get("/admin/inventory-alerts", { params: { limit: lim } });
      setInventoryAlerts(data?.lowStockProducts || []);
      setInventoryStats({
        totalProducts: data?.totalProducts ?? 0,
        alertCount: data?.alertCount ?? 0,
        lowStockLimit: data?.lowStockLimit ?? lim
      });
    } catch {
      setInventoryAlerts([]);
      setInventoryStats(null);
    }
  };

  const applyInventoryThreshold = async () => {
    const lim = Math.min(500, Math.max(0, Number(inventoryThreshold) || 10));
    setInventoryThreshold(lim);
    await loadInventoryAlerts(lim);
  };

  const saveProductStock = async (product) => {
    const raw = stockDraftById[product._id];
    const next = Math.max(0, Math.floor(Number(raw !== undefined ? raw : product.stock)));
    setUpdatingStockId(product._id);
    setStatusMessage("");
    try {
      await API.put(`/products/${product._id}`, { stock: next });
      setProducts((prev) => prev.map((p) => (p._id === product._id ? { ...p, stock: next } : p)));
      setStockDraftById((prev) => {
        const nextMap = { ...prev };
        delete nextMap[product._id];
        return nextMap;
      });
      setStatusMessage("Stock updated");
      await loadInventoryAlerts();
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Could not update stock");
    } finally {
      setUpdatingStockId("");
    }
  };

  const loadAdminData = async () => {
    try {
      const [ordersRes, productsRes, usersRes, analyticsRes, couponsRes, bannersRes, shopSettingsRes, notificationsRes, returnsRes, rolesRes, categoriesRes, reviewsRes] =
        await Promise.all([
          API.get("/orders"),
          API.get("/products"),
          API.get("/auth/users"),
          API.get("/admin/analytics"),
          API.get("/admin/coupons"),
          API.get("/admin/banners"),
          API.get("/admin/shop-settings"),
          API.get("/admin/notifications"),
          API.get("/admin/returns"),
          API.get("/admin/roles"),
          API.get("/categories"),
          API.get("/reviews/admin/all")
        ]);
      setOrders(ordersRes.data || []);
      setProducts(productsRes.data || []);
      setCustomers(usersRes.data?.users || []);
      setAnalytics(analyticsRes.data || null);
      setCoupons(couponsRes.data || []);
      setBanners(bannersRes.data || []);
      setBannerAnnouncementForm(String(shopSettingsRes.data?.bannerAnnouncement || ""));
      setNotifications(notificationsRes.data || []);
      setReturns(returnsRes.data || []);
      setRolesData(rolesRes.data?.users || []);
      setCategories(categoriesRes.data || []);
      setReviewsData(reviewsRes.data?.reviews || []);
      await loadInventoryAlerts();
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to load admin dashboard data");
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (!statusMessage) return;
    const id = window.setTimeout(() => setStatusMessage(""), 3000);
    return () => window.clearTimeout(id);
  }, [statusMessage]);

  useEffect(() => {
    if (activeTab !== "settings" || settingsSection !== "operations") return;
    API.get("/admin/returns")
      .then((res) => setReturns(res.data || []))
      .catch(() => setReturns([]));
  }, [activeTab, settingsSection]);

  useEffect(() => {
    if (activeTab !== "settings" || settingsSection !== "operations") return;
    loadInventoryAlerts();
  }, [activeTab, settingsSection]);

  const lowestStockPreview = useMemo(() => {
    const list = [...(products || [])];
    list.sort((a, b) => Number(a.stock ?? 0) - Number(b.stock ?? 0));
    return list.slice(0, 15);
  }, [products]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      setUpdatingOrderId(orderId);
      setStatusMessage("");
      const { data } = await API.put(`/orders/${orderId}/status`, { status });
      setOrders((prev) => prev.map((order) => (order._id === orderId ? data : order)));
      setStatusMessage(`Order ${String(orderId).slice(-6)} updated to ${status}`);
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to update order status");
    } finally {
      setUpdatingOrderId("");
    }
  };

  const updateReturnStatus = async (orderId, status) => {
    try {
      const { data } = await API.put(`/admin/returns/${orderId}`, { status });
      setReturns((prev) => prev.map((order) => (order._id === orderId ? data : order)));
      setOrders((prev) => prev.map((order) => (order._id === orderId ? data : order)));
      setStatusMessage(`Return for ${String(orderId).slice(-6)} set to ${status}`);
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to update return status");
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      await API.post("/admin/coupons", {
        ...couponForm,
        discountValue: Number(couponForm.discountValue || 0),
        minOrderAmount: Number(couponForm.minOrderAmount || 0),
        maxDiscountAmount: Number(couponForm.maxDiscountAmount || 0)
      });
      setCouponForm({ code: "", discountType: "PERCENT", discountValue: "", minOrderAmount: "", maxDiscountAmount: "", expiresAt: "" });
      const { data } = await API.get("/admin/coupons");
      setCoupons(data || []);
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to create coupon");
    }
  };

  const toggleCoupon = async (coupon) => {
    const { data } = await API.put(`/admin/coupons/${coupon._id}`, { isActive: !coupon.isActive });
    setCoupons((prev) => prev.map((item) => (item._id === coupon._id ? data : item)));
  };

  const deleteCoupon = async (id) => {
    await API.delete(`/admin/coupons/${id}`);
    setCoupons((prev) => prev.filter((item) => item._id !== id));
  };

  const createBanner = async (e) => {
    e.preventDefault();
    const image = String(bannerForm.image || "").trim();
    if (!image) {
      setStatusMessage("Image URL is required");
      return;
    }
    try {
      await API.post("/admin/banners", {
        image,
        title: "",
        subtitle: "",
        ctaText: "",
        ctaLink: ""
      });
      setBannerForm({ image: "" });
      const { data } = await API.get("/admin/banners");
      setBanners(data || []);
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to create banner");
    }
  };

  const deleteBanner = async (id) => {
    await API.delete(`/admin/banners/${id}`);
    setBanners((prev) => prev.filter((item) => item._id !== id));
  };

  const saveBannerAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await API.put("/admin/shop-settings", { bannerAnnouncement: bannerAnnouncementForm });
      setStatusMessage("Home announcement bar saved — refresh the storefront to see changes.");
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to save announcement bar");
    }
  };

  const sendNotification = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/admin/notifications", notifyForm);
      setNotifyForm({ target: "", subject: "", message: "" });
      const listRes = await API.get("/admin/notifications");
      setNotifications(listRes.data || []);
      if (data?.deliveryError || data?.status === "FAILED") {
        setStatusMessage(data?.deliveryError || data?.errorMessage || "Delivery failed — see log below");
      } else {
        setStatusMessage("Notification sent successfully");
      }
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to send notification");
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await API.put(`/auth/users/${userId}/role`, { role, permissions: role === "sub_admin" ? ["orders", "products"] : [] });
      const { data } = await API.get("/admin/roles");
      setRolesData(data?.users || []);
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to update role");
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    try {
      await API.post("/categories", { title: newCategory.trim() });
      setNewCategory("");
      const { data } = await API.get("/categories");
      setCategories(data || []);
      setStatusMessage("Main category added");
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to add category");
    }
  };

  const deleteCategory = async (id) => {
    try {
      await API.delete(`/categories/${id}`);
      const { data } = await API.get("/categories");
      setCategories(data || []);
      setStatusMessage("Main category deleted");
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to delete category");
    }
  };

  const addSubCategory = async (e) => {
    e.preventDefault();
    if (!selectedCategoryId || !newSubCategory.trim()) return;
    try {
      await API.post(`/categories/${selectedCategoryId}/subcategories`, { item: newSubCategory.trim() });
      setNewSubCategory("");
      const { data } = await API.get("/categories");
      setCategories(data || []);
      setStatusMessage("Sub-category added");
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to add sub-category");
    }
  };

  const deleteSubCategory = async (categoryId, subCategory) => {
    try {
      await API.delete(`/categories/${categoryId}/subcategories/${encodeURIComponent(subCategory)}`);
      const { data } = await API.get("/categories");
      setCategories(data || []);
      setStatusMessage("Sub-category deleted");
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to delete sub-category");
    }
  };

  const openInvoice = async (orderId) => {
    try {
      const { data } = await API.get(`/admin/invoices/${orderId}`);
      const lines = [
        `Invoice: ${data.invoiceNo}`,
        `Date: ${new Date(data.issueDate).toLocaleString()}`,
        `Customer: ${data.order.userId?.name || data.order.deliveryAddress?.fullName || "Customer"}`,
        `Email: ${data.order.userId?.email || "-"}`,
        `Phone: ${data.order.deliveryAddress?.phone || "-"}`,
        "",
        "Items:"
      ];
      (data.order.products || []).forEach((item) => {
        lines.push(`- ${item.name} x ${item.quantity} = ₹ ${Number(item.price) * Number(item.quantity)}`);
        orderLineExtras(item).forEach((ex) => lines.push(`    ${ex}`));
      });
      lines.push("", `Total Amount: ₹ ${data.order.totalAmount}`, `Payment Status: ${computedPaymentStatus(data.order)}`);
      const w = window.open("", "_blank");
      w.document.write(`<pre>${lines.join("\n")}</pre>`);
      w.document.close();
    } catch (error) {
      setStatusMessage(error?.response?.data?.message || "Failed to generate invoice");
    }
  };

  const revenueOrders = orders.filter((order) => order.status !== "Cancelled" && order.paymentStatus !== "Refunded");
  const totalRevenue = revenueOrders.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
  const pendingOrders = orders.filter((order) => ["Pending", "Placed"].includes(order.status)).length;
  const completedOrders = revenueOrders.filter((order) => order.status === "Delivered").length;
  const recentOrders = orders.slice(0, 6);
  const recentProducts = products.slice(0, 6);

  const filteredOrders = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const name = String(o.userId?.name || o.deliveryAddress?.fullName || "").toLowerCase();
      const email = String(o.userId?.email || "").toLowerCase();
      const id = String(o._id || "").toLowerCase();
      return name.includes(q) || email.includes(q) || id.includes(q.replace(/\s/g, ""));
    });
  }, [orders, adminSearch]);

  const filteredCustomers = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        String(c.name || "").toLowerCase().includes(q) ||
        String(c.email || "").toLowerCase().includes(q) ||
        String(c.phone || "").toLowerCase().includes(q)
    );
  }, [customers, adminSearch]);

  const growthPct = useMemo(() => {
    const m = analytics?.ordersByMonth || [];
    if (m.length < 2) return 0;
    const a = Number(m[m.length - 2]?.ordersCount) || 0;
    const b = Number(m[m.length - 1]?.ordersCount) || 0;
    if (!a) return b ? 100 : 0;
    return Math.round(((b - a) / a) * 100);
  }, [analytics]);

  const sidebarNav = [
    { id: "dashboard", label: "Dashboard", icon: "◇" },
    { id: "products", label: "Products", icon: "▤" },
    { id: "orders", label: "Orders", icon: "◎" },
    { id: "customers", label: "Customers", icon: "◉" },
    { id: "coupons", label: "Coupons", icon: "%" },
    { id: "analytics", label: "Analytics", icon: "▣" },
    { id: "ai-tools", label: "AI Tools", icon: "✦", badge: "NEW" },
    { id: "settings", label: "Settings", icon: "⚙" }
  ];

  const setMainTab = (id) => {
    setActiveTab(id);
    if (id !== "products") setProductsSubview("list");
    setProfileOpen(false);
  };

  const navigate = useNavigate();

  return (
    <div className="admin-premium-root">
      <div className="admin-premium-body">
        <aside className="admin-premium-sidebar">
          <div className="admin-premium-brand">
            <div className="admin-premium-brand-mark">C</div>
            <div className="admin-premium-brand-text">
              <strong>Corrucraft</strong>
              <small>Admin Console</small>
            </div>
          </div>
          <nav className="admin-premium-nav" aria-label="Admin sections">
            {sidebarNav.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={activeTab === tab.id ? "active" : ""}
                onClick={() => setMainTab(tab.id)}
              >
                <span className="admin-premium-nav-ico" aria-hidden>
                  {tab.icon}
                </span>
                {tab.label}
                {tab.badge ? <span className="admin-premium-nav-badge">{tab.badge}</span> : null}
              </button>
            ))}
          </nav>
        </aside>
        <div className="admin-premium-main-wrap">
          <header className="admin-premium-topbar">
            <div className="admin-premium-search">
              <span className="admin-premium-search-icon" aria-hidden>
                ⌕
              </span>
              <input
                type="search"
                placeholder="Search orders, products, customers…"
                value={adminSearch}
                onChange={(e) => setAdminSearch(e.target.value)}
                aria-label="Global admin search"
              />
            </div>
            <div className="admin-premium-top-actions">
              <button type="button" className="admin-premium-icon-btn" aria-label="Notifications">
                <span aria-hidden>🔔</span>
                {notifications.length ? <span className="admin-premium-notif-dot" /> : null}
              </button>
              <div className="admin-premium-profile-wrap">
                <button
                  type="button"
                  className="admin-premium-profile-trigger"
                  onClick={() => setProfileOpen((o) => !o)}
                  aria-expanded={profileOpen}
                >
                  <span className="admin-premium-avatar">{(user?.name || user?.email || "A").slice(0, 1).toUpperCase()}</span>
                  <span>{user?.name || "Admin"}</span>
                </button>
                {profileOpen ? (
                  <div className="admin-premium-dropdown">
                    <Link to="/" onClick={() => setProfileOpen(false)}>
                      View storefront
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        persistLogoutCartCleanup();
                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                        navigate("/auth", { replace: true });
                      }}
                    >
                      Log out
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </header>
          <main className="admin-premium-content">
            {statusMessage ? <div className="admin-premium-toast">{statusMessage}</div> : null}
            {activeTab === "settings" && (
              <div className="glass-panel" style={{ padding: "16px 20px" }}>
                <h2 style={{ margin: "0 0 12px", fontSize: 18, color: "#f8fafc" }}>Settings</h2>
                <div className="admin-premium-settings-pills">
                  {[
                    { id: "store", label: "Storefront" },
                    { id: "notifications", label: "Email" },
                    { id: "operations", label: "Operations" },
                    { id: "reviews", label: "Reviews" },
                    { id: "pricing", label: "Pricing" },
                    { id: "team", label: "Team" },
                    { id: "catalog", label: "Catalog" }
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={settingsSection === p.id ? "active" : ""}
                      onClick={() => setSettingsSection(p.id)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeTab === "ai-tools" && (
              <AIProductDescriptionGenerator
                categories={categories.map((c) => c.title)}
                onApplyDescription={(text) => {
                  navigator.clipboard?.writeText(text).catch(() => {});
                  setStatusMessage("Description copied to clipboard — open Products → New product to paste.");
                }}
              />
            )}
            {activeTab === "dashboard" && (
              <div className="admin-premium-kpi-grid">
                <div className="glass-panel glass-panel--lift admin-premium-kpi">
                  <p className="admin-premium-kpi-label">Revenue</p>
                  <p className="admin-premium-kpi-value">₹ {totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p>
                  <p className="admin-premium-kpi-hint">Excluding cancelled / refunded</p>
                </div>
                <div className="glass-panel glass-panel--lift admin-premium-kpi">
                  <p className="admin-premium-kpi-label">Orders</p>
                  <p className="admin-premium-kpi-value">{orders.length}</p>
                  <p className="admin-premium-kpi-hint">{pendingOrders} pending · {completedOrders} delivered</p>
                </div>
                <div className="glass-panel glass-panel--lift admin-premium-kpi">
                  <p className="admin-premium-kpi-label">Users</p>
                  <p className="admin-premium-kpi-value">{customers.length}</p>
                  <p className="admin-premium-kpi-hint">Registered customers</p>
                </div>
                <div className="glass-panel glass-panel--lift admin-premium-kpi admin-premium-kpi--growth">
                  <p className="admin-premium-kpi-label">Growth</p>
                  <p className="admin-premium-kpi-value">{growthPct >= 0 ? "+" : ""}
                    {growthPct}%
                  </p>
                  <p className="admin-premium-kpi-hint">Orders vs last month in analytics</p>
                </div>
              </div>
            )}

            {activeTab === "dashboard" && <AdminAnalyticsCharts analytics={analytics} orders={orders} />}

            {activeTab === "dashboard" && (
          <div className="admin-dashboard-grid">
            <div className="glass-panel admin-table-card">
              <h3>Recent Orders</h3>
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Id</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                    <tr key={order._id}>
                      <td>{order.userId?.name || order.deliveryAddress?.fullName || "Customer"}</td>
                      <td>{String(order._id).slice(-6)}</td>
                      <td>₹ {order.totalAmount}</td>
                      <td>{order.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="glass-panel admin-table-card">
              <h3>Recent Products</h3>
              <table>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {recentProducts.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <div className="admin-table-thumb">
                          {product.image ? <img src={product.image} alt={product.name} /> : <span>{product.name.slice(0, 2)}</span>}
                        </div>
                      </td>
                      <td>{product.name}</td>
                      <td>{product.category}</td>
                      <td>{product.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="glass-panel admin-table-card">
            <h3>Advanced Analytics</h3>
            <p>Total Sales: {analytics?.totalSales || 0}</p>
            <p>Total Revenue: ₹ {Number(analytics?.totalRevenue || 0).toFixed(2)}</p>
            <h4>Orders Per Month</h4>
            {(analytics?.ordersByMonth || []).map((entry) => (
              <p key={entry.month}>
                {entry.month}: {entry.ordersCount}
              </p>
            ))}
            <h4>Top Selling Products</h4>
            {(analytics?.topProducts || []).map((item) => (
              <p key={item.name}>
                {item.name}: {item.quantity}
              </p>
            ))}
          </div>
        )}

        {activeTab === "orders" && (
          <div className="glass-panel admin-table-card">
            <div className="admin-premium-products-toolbar">
              <h3 style={{ margin: 0 }}>All Orders</h3>
              <span className="admin-premium-kpi-hint" style={{ margin: 0 }}>
                {filteredOrders.length} shown
              </span>
            </div>
            <div className="admin-premium-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment Method</th>
                  <th>Payment Status</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <Fragment key={order._id}>
                    <tr>
                    <td>{String(order._id).slice(-8)}</td>
                    <td>{order.userId?.name || order.deliveryAddress?.fullName || "Customer"}</td>
                    <td>
                      <div>{order.userId?.email || "No email"}</div>
                      <div>{order.deliveryAddress?.phone || "No phone"}</div>
                    </td>
                    <td>
                      {orderLocationLink(order) ? (
                        <a
                          href={orderLocationLink(order)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {order.deliveryAddress?.latitude && order.deliveryAddress?.longitude ? "Exact Location" : "View on Map"}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {(order.products || []).map((item, index) => (
                        <div key={`${item.productId}-${index}`} className="admin-order-line">
                          <div>
                            {item.name} x {item.quantity}
                          </div>
                          {orderLineExtras(item).map((line, li) => (
                            <div key={`${index}-c-${li}`} className="admin-order-custom">
                              {line}
                            </div>
                          ))}
                          {item.customImage?.trim() ? (
                            <a href={item.customImage.trim()} target="_blank" rel="noreferrer" className="admin-order-custom-img-wrap">
                              <img src={item.customImage.trim()} alt="" className="admin-order-custom-img" />
                            </a>
                          ) : null}
                        </div>
                      ))}
                    </td>
                    <td>₹ {order.totalAmount}</td>
                    <td>{order.paymentMethod}</td>
                    <td>{computedPaymentStatus(order)}</td>
                    <td>
                      <span className={statusBadgeClass(order.status)} style={{ marginBottom: 8, display: "inline-flex" }}>
                        {order.status}
                      </span>
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                        disabled={updatingOrderId === order._id}
                      >
                        {orderStatuses.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button type="button" onClick={() => openInvoice(order._id)}>
                        Download
                      </button>
                    </td>
                  </tr>
                    <tr className="admin-order-timeline-row">
                      <td colSpan={10} style={{ background: "rgba(2,6,23,0.35)" }}>
                        <AdminOrderTimeline status={order.status} />
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "operations" && (
          <div className="glass-panel admin-table-card">
            <h3>Returns & Refunds</h3>
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Reason</th>
                  <th>Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((order) => (
                  <tr key={order._id}>
                    <td>{String(order._id).slice(-8)}</td>
                    <td>{order.userId?.name || "Customer"}</td>
                    <td>{order.returnRequest?.reason || "-"}</td>
                    <td>
                      {orderLocationLink(order) ? (
                        <a
                          href={orderLocationLink(order)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {order.deliveryAddress?.latitude && order.deliveryAddress?.longitude ? "Exact Location" : "View on Map"}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <select
                        value={order.returnRequest?.status || "Requested"}
                        onChange={(e) => updateReturnStatus(order._id, e.target.value)}
                      >
                        {returnStatuses.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {!returns.length && (
                  <tr>
                    <td colSpan={5}>
                      <small>No return requests yet.</small>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "operations" && (
          <div className="glass-panel admin-table-card">
            <h3>Inventory Alerts</h3>
            <p className="admin-inventory-intro">
              Products at or below the threshold count as <strong>low stock</strong>. Total products in catalog:{" "}
              {inventoryStats?.totalProducts ?? products.length}. Adjust the threshold and click <strong>Apply</strong> to refresh.
            </p>
            <div className="admin-inventory-toolbar">
              <label className="admin-inventory-label">
                Low-stock threshold (units)
                <input
                  type="number"
                  min={0}
                  max={500}
                  value={inventoryThreshold}
                  onChange={(e) => setInventoryThreshold(Number(e.target.value))}
                />
              </label>
              <button type="button" onClick={applyInventoryThreshold}>
                Apply
              </button>
              <button type="button" className="btn-secondary" onClick={() => loadInventoryAlerts()}>
                Refresh
              </button>
            </div>
            {inventoryStats ? (
              <p className="admin-inventory-summary">
                Showing <strong>{inventoryStats.alertCount}</strong> SKU(s) with stock ≤ <strong>{inventoryStats.lowStockLimit}</strong>
              </p>
            ) : null}
            <div className="admin-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Subcategory</th>
                    <th>Status</th>
                    <th>Stock</th>
                    <th>Update</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryAlerts.map((item) => {
                    const stock = Number(item.stock ?? 0);
                    const status =
                      stock <= 0 ? "Out of stock" : stock <= (inventoryStats?.lowStockLimit ?? inventoryThreshold) ? "Low stock" : "OK";
                    return (
                      <tr key={item._id}>
                        <td>{item.name}</td>
                        <td>{item.category}</td>
                        <td>{item.subCategory}</td>
                        <td>
                          <span
                            className={`admin-stock-pill ${
                              stock <= 0 ? "critical" : stock <= (inventoryStats?.lowStockLimit ?? inventoryThreshold) ? "warn" : ""
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                        <td>
                          <input
                            className="admin-stock-input"
                            type="number"
                            min={0}
                            value={stockDraftById[item._id] !== undefined ? stockDraftById[item._id] : String(stock)}
                            onChange={(e) =>
                              setStockDraftById((prev) => ({
                                ...prev,
                                [item._id]: e.target.value
                              }))
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            disabled={updatingStockId === item._id}
                            onClick={() => saveProductStock(item)}
                          >
                            {updatingStockId === item._id ? "Saving…" : "Save"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!inventoryAlerts.length && (
              <p className="admin-inventory-empty">
                No products match this threshold — either all SKUs are above <strong>{inventoryStats?.lowStockLimit ?? inventoryThreshold}</strong>{" "}
                units, or try raising the threshold. Use the preview below to see the lowest-stocked items overall.
              </p>
            )}
            <h4 className="admin-inventory-subhead">Lowest stock — all products (preview)</h4>
            <p className="admin-inventory-hint">Always shows the 15 lowest SKUs by current stock (from your catalog).</p>
            <div className="admin-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {lowestStockPreview.map((item) => (
                    <tr key={`prev-${item._id}`}>
                      <td>{item.name}</td>
                      <td>{item.category}</td>
                      <td>{item.stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!lowestStockPreview.length && <p>No products in catalog.</p>}
          </div>
        )}

        {activeTab === "coupons" && (
          <div className="glass-panel admin-table-card">
            <h3>Coupon & Discount System</h3>
            <form className="address-form" onSubmit={createCoupon}>
              <input placeholder="Coupon Code" value={couponForm.code} onChange={(e) => setCouponForm((p) => ({ ...p, code: e.target.value }))} required />
              <select value={couponForm.discountType} onChange={(e) => setCouponForm((p) => ({ ...p, discountType: e.target.value }))}>
                <option value="PERCENT">% Discount</option>
                <option value="FLAT">Flat Discount</option>
              </select>
              <input placeholder="Discount Value" type="number" value={couponForm.discountValue} onChange={(e) => setCouponForm((p) => ({ ...p, discountValue: e.target.value }))} required />
              <input placeholder="Min Order Amount" type="number" value={couponForm.minOrderAmount} onChange={(e) => setCouponForm((p) => ({ ...p, minOrderAmount: e.target.value }))} />
              <input placeholder="Max Discount Amount" type="number" value={couponForm.maxDiscountAmount} onChange={(e) => setCouponForm((p) => ({ ...p, maxDiscountAmount: e.target.value }))} />
              <input type="datetime-local" value={couponForm.expiresAt} onChange={(e) => setCouponForm((p) => ({ ...p, expiresAt: e.target.value }))} required />
              <button type="submit">Create Coupon</button>
            </form>
            {coupons.map((coupon) => (
              <div key={coupon._id} className="admin-product-item">
                <div>
                  <strong>{coupon.code}</strong>
                  <p>
                    {coupon.discountType} {coupon.discountValue} | Expires: {new Date(coupon.expiresAt).toLocaleString()}
                  </p>
                </div>
                <div className="admin-item-actions">
                  <button type="button" onClick={() => toggleCoupon(coupon)}>
                    {coupon.isActive ? "Disable" : "Enable"}
                  </button>
                  <button type="button" className="btn-danger" onClick={() => deleteCoupon(coupon._id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "settings" && settingsSection === "store" && (
          <div className="glass-panel admin-table-card">
            <h3>Banner & Offer Management</h3>
            <h4 className="admin-subsection-title">Announcement bar (under home banner)</h4>
            <p className="admin-hint">Shown to all visitors below the image carousel. Leave empty to hide the bar. Text scrolls left-to-right in a loop.</p>
            <form className="address-form" onSubmit={saveBannerAnnouncement}>
              <label htmlFor="banner-announcement-input">Message (max 600 characters)</label>
              <textarea
                id="banner-announcement-input"
                placeholder="e.g. Free shipping on orders over ₹999 — Shop wooden gifts now!"
                value={bannerAnnouncementForm}
                onChange={(e) => setBannerAnnouncementForm(e.target.value)}
                maxLength={600}
                rows={2}
              />
              <button type="submit">Save announcement bar</button>
            </form>
            <form className="address-form" onSubmit={createBanner}>
              <label htmlFor="home-banner-image-url">Image URL</label>
              <input
                id="home-banner-image-url"
                placeholder="Image URL"
                value={bannerForm.image}
                onChange={(e) => setBannerForm({ image: e.target.value })}
                required
              />
              <button type="submit">Create Banner</button>
            </form>
            {banners.map((banner) => (
              <div key={banner._id} className="admin-product-item">
                <div className="admin-banner-row">
                  {banner.image ? (
                    <img className="admin-banner-thumb" src={banner.image} alt="" />
                  ) : null}
                  <div>
                    <strong>Carousel slide</strong>
                    <p className="admin-banner-url">{banner.image || "—"}</p>
                  </div>
                </div>
                <div className="admin-item-actions">
                  <button type="button" className="btn-danger" onClick={() => deleteBanner(banner._id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "settings" && settingsSection === "notifications" && (
          <div className="glass-panel admin-table-card">
            <h3>Email notifications</h3>
            <form className="address-form" onSubmit={sendNotification}>
              <label htmlFor="notify-email">Recipient email</label>
              <input id="notify-email" type="email" placeholder="customer@example.com" value={notifyForm.target} onChange={(e) => setNotifyForm((p) => ({ ...p, target: e.target.value }))} required />
              <label htmlFor="notify-subject">Subject</label>
              <input id="notify-subject" placeholder="Subject" value={notifyForm.subject} onChange={(e) => setNotifyForm((p) => ({ ...p, subject: e.target.value }))} required />
              <label htmlFor="notify-message">Message</label>
              <textarea id="notify-message" placeholder="Message" value={notifyForm.message} onChange={(e) => setNotifyForm((p) => ({ ...p, message: e.target.value }))} required />
              <button type="submit">Send email</button>
            </form>
            {notifications.map((item) => (
              <div key={item._id} className="admin-product-item">
                <div>
                  <strong>{item.channel}</strong>
                  <p>
                    {item.target} | {item.status}
                    {item.errorMessage ? ` — ${item.errorMessage}` : ""}
                  </p>
                </div>
                <small>{new Date(item.createdAt).toLocaleString()}</small>
              </div>
            ))}
          </div>
        )}

        {activeTab === "settings" && settingsSection === "reviews" && (
          <div className="glass-panel admin-table-card">
            <h3>Review Moderation</h3>
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>User</th>
                  <th>Rating</th>
                  <th>Comment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {reviewsData.map((review) => (
                  <tr key={review._id}>
                    <td>{review.productId?.name || "Product"}</td>
                    <td>{review.userId?.name || review.userId?.email || "User"}</td>
                    <td>{review.rating}</td>
                    <td>{review.comment || "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={async () => {
                          try {
                            await API.delete(`/reviews/admin/${review._id}`);
                            setReviewsData((prev) => prev.filter((r) => r._id !== review._id));
                            setStatusMessage("Review deleted.");
                          } catch (error) {
                            setStatusMessage(error?.response?.data?.message || "Failed to delete review");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {!reviewsData.length && (
                  <tr>
                    <td colSpan={5}>
                      <small>No reviews available.</small>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "pricing" && (
          <div className="glass-panel admin-table-card">
            <h3>Pricing Controls</h3>
            <p>Set global tax percentage and delivery fee for checkout totals.</p>
            <form
              className="address-form"
              onSubmit={(e) => {
                e.preventDefault();
                const next = {
                  taxPercent: Math.max(0, Number(pricingForm.taxPercent || 0)),
                  deliveryFee: Math.max(0, Number(pricingForm.deliveryFee || 0))
                };
                setPricingForm(next);
                localStorage.setItem("adminPricingConfig", JSON.stringify(next));
                setStatusMessage("Pricing settings saved successfully.");
              }}
            >
              <label>Tax Percentage (%)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pricingForm.taxPercent}
                onChange={(e) => setPricingForm((prev) => ({ ...prev, taxPercent: e.target.value }))}
                placeholder="e.g. 18"
              />
              <label>Delivery Fee (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pricingForm.deliveryFee}
                onChange={(e) => setPricingForm((prev) => ({ ...prev, deliveryFee: e.target.value }))}
                placeholder="e.g. 50"
              />
              <button type="submit">Save Pricing</button>
            </form>
            <small>
              Current Applied: Tax {Number(pricingForm.taxPercent || 0)}% | Delivery Fee ₹ {Number(pricingForm.deliveryFee || 0)}
            </small>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "team" && (
          <div className="glass-panel admin-table-card">
            <h3>Admin Roles & Permissions</h3>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {rolesData.map((item) => (
                  <tr key={item._id}>
                    <td>{item.name}</td>
                    <td>{item.email}</td>
                    <td>
                      {isSuperAdmin ? (
                        <select value={item.role || "customer"} onChange={(e) => updateRole(item._id, e.target.value)}>
                          {roleOptions.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      ) : (
                        item.role || "customer"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "catalog" && (
          <div className="glass-panel admin-table-card">
            <h3>Category Manager</h3>
            <form className="address-form" onSubmit={createCategory}>
              <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Add Main Category" required />
              <button type="submit">Add Main Category</button>
            </form>
            <form className="address-form" onSubmit={addSubCategory}>
              <select value={selectedCategoryId} onChange={(e) => setSelectedCategoryId(e.target.value)} required>
                <option value="">Select main category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.title}
                  </option>
                ))}
              </select>
              <input value={newSubCategory} onChange={(e) => setNewSubCategory(e.target.value)} placeholder="Add Sub Category" required />
              <button type="submit">Add Sub Category</button>
            </form>
            <div className="admin-product-list">
              {categories.map((category) => (
                <div key={category._id} className="admin-product-item">
                  <div>
                    <strong>{category.title}</strong>
                    <p>{(category.items || []).join(", ") || "No sub-categories yet"}</p>
                  </div>
                  <div className="admin-item-actions">
                    <button type="button" className="btn-danger" onClick={() => deleteCategory(category._id)}>
                      Delete Main
                    </button>
                    {(category.items || []).map((item) => (
                      <button key={`${category._id}-${item}`} type="button" onClick={() => deleteSubCategory(category._id, item)}>
                        Delete {item}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "customers" && (
          <div className="glass-panel admin-table-card">
            <div className="admin-premium-products-toolbar">
              <h3 style={{ margin: 0 }}>Customers</h3>
              <span className="admin-premium-kpi-hint" style={{ margin: 0 }}>
                {filteredCustomers.length} shown
              </span>
            </div>
            <div className="admin-premium-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer._id}>
                    <td>{customer.name}</td>
                    <td>{customer.email}</td>
                    <td>{customer.phone}</td>
                    <td>{new Date(customer.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {activeTab === "products" && productsSubview === "list" && (
          <AdminProducts
            mode="products"
            listFilter={adminSearch}
            onNavigateAdd={() => setProductsSubview("add")}
          />
        )}
        {activeTab === "products" && productsSubview === "add" && (
          <>
            <div className="glass-panel" style={{ padding: "14px 18px", display: "flex", alignItems: "center" }}>
              <button type="button" className="admin-premium-btn-ghost" onClick={() => setProductsSubview("list")}>
                ← Back to catalog
              </button>
            </div>
            <AdminProducts mode="add-product" />
          </>
        )}
          </main>
        </div>
      </div>
    </div>
  );
};

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    API.get("/orders/my-orders")
      .then((res) => setOrders(res.data || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="card">Loading your orders...</div>;
  }

  const paymentStatus = (order) => {
    if (order.paymentStatus) return order.paymentStatus;
    if (order.paymentMethod === "Cash On Delivery") return order.status === "Delivered" ? "Paid" : "Pending";
    return order.paymentId ? "Paid" : "Pending";
  };

  const requestReturn = async (orderId) => {
    try {
      const reason = window.prompt("Please enter return reason:");
      if (!reason) return;
      const { data } = await API.post(`/orders/my-orders/${orderId}/return`, { reason });
      setOrders((prev) => prev.map((order) => (order._id === orderId ? data : order)));
      setMessage("Return request submitted.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to request return");
    }
  };

  const cancelReturnRequest = async (orderId) => {
    try {
      const ok = window.confirm("Cancel the return request?");
      if (!ok) return;
      const { data } = await API.put(`/orders/my-orders/${orderId}/return/cancel`);
      setOrders((prev) => prev.map((order) => (order._id === orderId ? data : order)));
      setMessage("Return request cancelled.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to cancel return request");
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      const ok = window.confirm("Cancel this order?");
      if (!ok) return;
      const { data } = await API.put(`/orders/my-orders/${orderId}/cancel`);
      setOrders((prev) => prev.map((order) => (order._id === orderId ? data : order)));
      setMessage("Order cancelled.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to cancel order");
    }
  };

  return (
    <div className="orders-page">
      <div className="card">
        <h2>My Orders</h2>
        {!orders.length && <p>No orders yet. Start shopping to place your first order.</p>}
        {message && <small>{message}</small>}
      </div>
      {orders.map((order) => (
        <div key={order._id} className="card my-order-card">
          <div className="my-order-head">
            <div>
              <strong>Order #{String(order._id).slice(-8)}</strong>
              <p>{new Date(order.createdAt).toLocaleString()}</p>
            </div>
            <span className={`order-status-chip ${String(order.status || "").toLowerCase().replace(/\s+/g, "-")}`}>
              {order.status}
            </span>
          </div>
          <div className="my-order-items">
            {(order.products || []).map((item, idx) => (
              <div key={`${item.productId}-${idx}`} className="my-order-item">
                <div className="my-order-item-left">
                  {item.image ? <img src={item.image} alt={item.name} /> : <span>{item.name.slice(0, 2).toUpperCase()}</span>}
                </div>
                <div>
                  <strong>{item.name}</strong>
                  <p>
                    Qty: {item.quantity} | ₹ {item.price}
                  </p>
                  {orderLineExtras(item).map((line, li) => (
                    <p key={`${idx}-ex-${li}`} className="order-item-extra">
                      {line}
                    </p>
                  ))}
                  {item.customImage?.trim() ? (
                    <a href={item.customImage.trim()} target="_blank" rel="noreferrer">
                      <img className="order-custom-thumb" src={item.customImage.trim()} alt="Custom reference" />
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div className="my-order-foot">
            <div>
              <strong>Delivery Address</strong>
              <p>
                {order.deliveryAddress?.fullName} - {order.deliveryAddress?.phone}
              </p>
              <p>
                {order.deliveryAddress?.line1}
                {order.deliveryAddress?.line2 ? `, ${order.deliveryAddress.line2}` : ""}, {order.deliveryAddress?.city},{" "}
                {order.deliveryAddress?.state} - {order.deliveryAddress?.pincode}
              </p>
            </div>
            <div className="my-order-total">
              <p>Payment: {order.paymentMethod}</p>
              <p>Status: {paymentStatus(order)}</p>
              {order.returnRequest?.requested ? (
                <>
                  <p>Return: {order.returnRequest.status}</p>
                  {order.returnRequest.status === "Requested" && (
                    <button type="button" className="btn-secondary" onClick={() => cancelReturnRequest(order._id)}>
                      Cancel Return Request
                    </button>
                  )}
                </>
              ) : (
                order.status === "Delivered" && (
                  <button type="button" onClick={() => requestReturn(order._id)}>
                    Request Return
                  </button>
                )
              )}
              <strong>Amount: ₹ {Number(order.totalAmount || 0).toFixed(2)}</strong>
              <button type="button" onClick={() => navigate(`/orders/success/${order._id}`)}>
                Track Order
              </button>
              {["Pending", "Placed", "Order Confirmed"].includes(order.status) && (
                <button type="button" className="btn-danger" onClick={() => cancelOrder(order._id)}>
                  Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const FOOTER_STATIC_PAGES = {
  "shipping-policy": {
    title: "Shipping Policy",
    paragraphs: [
      "Corrucraft Industries ships orders across India. Orders are usually dispatched within 1–3 business days after payment confirmation (or order confirmation for Cash on Delivery).",
      "Delivery timelines depend on your location and courier partner availability. You will receive tracking details by SMS or email when your order ships.",
      "Shipping charges, if any, are shown at checkout before you pay. Remote or restricted areas may require additional delivery time.",
      "If a product is delayed or unavailable, our team will contact you using the phone number or email on your account."
    ]
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    paragraphs: [
      "By using this website and placing orders, you agree to these terms. Product descriptions, prices, and availability may change without prior notice; the details shown at checkout apply to your purchase.",
      "You are responsible for providing accurate delivery and contact information. We are not liable for failed delivery caused by incorrect addresses or unreachable recipients.",
      "All content on this site (logos, images, text) is owned by Corrucraft Industries or its licensors and may not be copied without permission.",
      "We may suspend or restrict access to the site for maintenance, security, or legal reasons. These terms are governed by applicable laws in India."
    ]
  },
  "cancellation-refund-policy": {
    title: "Cancellation & Refund Policy",
    paragraphs: [
      "You may cancel an order before it is shipped by contacting support with your order ID. Once shipped, cancellation follows our return policy.",
      "For eligible returns (damaged, defective, or wrong item received), notify us within 48 hours of delivery with photos where applicable. Approved refunds are processed to the original payment method within reasonable banking timelines.",
      "Cash on Delivery refunds, if applicable, may be issued via bank transfer or store credit as per process communicated by support.",
      "Customized or personalized items may not be eligible for return unless there is a manufacturing defect."
    ]
  },
  "about-us": {
    title: "About Us",
    paragraphs: [
      "Corrucraft Industries (CCI) offers quality products across beauty, grocery, packaging, wooden goods, and custom gift categories — built around reliable sourcing and customer trust.",
      "We focus on clear communication, safe packaging, and timely delivery so shopping with us is straightforward from browse to doorstep.",
      "Our team is based in Abohar, Punjab, and we are committed to improving your experience continuously."
    ]
  },
  "contact-us": {
    title: "Contact Us",
    paragraphs: [
      "Address: Killianwali road, Mohan nagar-Z203638, Abohar, Punjab 152116, India",
      "Phone: +91 88475 22570, +91 98765 73021",
      "Email: Corrucraftindustries@gmail.com",
      "Business hours: Monday–Saturday, 10:00 AM – 6:00 PM IST. We aim to respond to emails within 1–2 working days."
    ]
  },
  blogs: {
    title: "Blogs",
    paragraphs: [
      "Tips, product stories, and updates from Corrucraft Industries will appear here. New articles are added periodically — check back soon.",
      "For partnership or content enquiries, write to Corrucraftindustries@gmail.com with the subject line “Blog enquiry”."
    ]
  }
};

const StaticInfoPage = () => {
  const { slug } = useParams();
  const page = FOOTER_STATIC_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;
  return (
    <div className="static-info-page">
      <div className="card static-info-card">
        <Link to="/" className="static-info-back">
          ← Back to home
        </Link>
        <h1 className="static-info-title">{page.title}</h1>
        <div className="static-info-body">
          {page.paragraphs.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const location = useLocation();
  const navigate = useNavigate();
  const wishlistCount = JSON.parse(localStorage.getItem("wishlist") || "[]").length;
  const token = localStorage.getItem("token");
  const isLoggedIn = !!user && !!token;
  const [cartCount, setCartCount] = useState(() =>
    readPersistedCart().reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
  );
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [categoryMenus, setCategoryMenus] = useState(DEFAULT_CATEGORY_MENUS);
  const isAdminRoute = location.pathname === "/admin";

  useEffect(() => {
    const { pathname, search } = location;

    if (pathname === "/" || pathname === "") {
      document.title = formatPageTitle("Home");
      return;
    }
    if (pathname.startsWith("/category/")) {
      const raw = pathname.slice("/category/".length).replace(/\/+$/, "");
      let category = "Products";
      try {
        category = decodeURIComponent(raw || category) || category;
      } catch {
        category = raw || category;
      }
      const sub = new URLSearchParams(search).get("sub");
      let segment = category;
      if (sub) {
        try {
          segment = decodeURIComponent(sub);
        } catch {
          segment = sub;
        }
      }
      document.title = formatPageTitle(segment);
      return;
    }
    if (pathname.startsWith("/product/")) {
      document.title = formatPageTitle("Product");
      return;
    }
    if (pathname.startsWith("/info/")) {
      const slug = pathname.replace(/^\/info\//, "").replace(/\/+$/, "");
      const page = FOOTER_STATIC_PAGES[slug];
      const label = page?.title || slug.replace(/-/g, " ");
      document.title = formatPageTitle(label);
      return;
    }
    if (pathname === "/auth") {
      document.title = formatPageTitle("Login");
      return;
    }
    if (pathname === "/profile") {
      document.title = formatPageTitle("Profile");
      return;
    }
    if (pathname === "/wishlist") {
      document.title = formatPageTitle("Wishlist");
      return;
    }
    if (pathname === "/checkout") {
      document.title = formatPageTitle("Shopping cart");
      return;
    }
    if (pathname.startsWith("/orders/success/")) {
      document.title = formatPageTitle("Order confirmed");
      return;
    }
    if (pathname === "/orders") {
      document.title = formatPageTitle("My orders");
      return;
    }
    if (pathname === "/admin") {
      document.title = formatPageTitle("Admin");
      return;
    }

    document.title = SITE_BRAND_TITLE;
  }, [location]);

  const onOpenCategory = (category, subCategory = "") => {
    const path = `/category/${encodeURIComponent(category)}${subCategory ? `?sub=${encodeURIComponent(subCategory)}` : ""}`;
    navigate(path);
  };

  useEffect(() => {
    setCartCount(readPersistedCart().reduce((sum, item) => sum + (Number(item.quantity) || 0), 0));
  }, [location.pathname, isLoggedIn]);

  useEffect(() => {
    const onCartUpdated = () => {
      setCartCount(readPersistedCart().reduce((sum, item) => sum + (Number(item.quantity) || 0), 0));
    };
    window.addEventListener("cc-cart-updated", onCartUpdated);
    return () => window.removeEventListener("cc-cart-updated", onCartUpdated);
  }, []);

  useEffect(() => {
    // Always show auth popup for guests on any non-auth page
    if (!isLoggedIn && location.pathname !== "/auth" && location.pathname !== "/admin") {
      setShowAuthPopup(true);
    } else {
      setShowAuthPopup(false);
    }
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    API.get("/products")
      .then((res) => {
        const terms = (res.data || []).map((item) => item.name).filter(Boolean).slice(0, 50);
        setSearchSuggestions(terms);
      })
      .catch(() => setSearchSuggestions([]));
    API.get("/categories")
      .then((res) => {
        const menus = (res.data || []).map((item) => ({ title: item.title, items: item.items || [] }));
        if (menus.length) setCategoryMenus(menus);
      })
      .catch(() => setCategoryMenus(DEFAULT_CATEGORY_MENUS));
  }, []);

  const handlePointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    e.currentTarget.style.setProperty("--pointer-x", `${(x * 8).toFixed(2)}px`);
    e.currentTarget.style.setProperty("--pointer-y", `${(y * 8).toFixed(2)}px`);
  };

  const handlePointerLeave = (e) => {
    e.currentTarget.style.setProperty("--pointer-x", "0px");
    e.currentTarget.style.setProperty("--pointer-y", "0px");
  };

  return (
    <div
      className={isAdminRoute ? "admin-route-standalone" : "app-layout"}
      onMouseMove={isAdminRoute ? undefined : handlePointerMove}
      onMouseLeave={isAdminRoute ? undefined : handlePointerLeave}
    >
      {!isAdminRoute ? (
      <div className="sticky-nav-stack">
        <nav className="main-nav">
          <Link to="/" className="brand-logo" aria-label="Corrucraft Industries — Go to home">
            <div className="brand-blocks">
              <span className="brand-box box-brown">C</span>
              <span className="brand-box box-black">C</span>
              <span className="brand-box box-red">I</span>
            </div>
            <span className="brand-name">CORRUCRAFT INDUSTRIES</span>
          </Link>
          <div className="nav-links nav-pill">
            <Link to="/">Home</Link>
            <Link to="/" onClick={() => {
  setTimeout(() => {
    document.getElementById("explore-categories")?.scrollIntoView({ behavior: "smooth" });
  }, 100);
}}>
  Products
</Link>
            <Link to={isLoggedIn ? "/profile" : "/auth"}>{isLoggedIn ? "Profile" : "Login"}</Link>
          </div>
          <input
            className="search"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            list="search-suggestions"
          />
          <datalist id="search-suggestions">
            {searchSuggestions.map((term) => (
              <option key={term} value={term} />
            ))}
          </datalist>
          <div className="account-cart">
            <Link to={isLoggedIn ? "/profile" : "/auth"} className="icon-nav">
              <span className="icon">👤</span>
              <span className="icon-label">{isLoggedIn ? "My Account" : "Login"}</span>
            </Link>
            <Link to={isLoggedIn ? "/wishlist" : "/auth"} className="icon-nav cart-icon">
              <span className="icon">♡</span>
              {wishlistCount > 0 && <span className="cart-badge">{wishlistCount}</span>}
              <span className="icon-label">Wish List</span>
            </Link>
            <Link to={isLoggedIn ? "/checkout" : "/auth"} className="icon-nav cart-icon">
              <span className="icon">🛒</span>
              <span className="cart-badge">{cartCount}</span>
              <span className="icon-label">Cart</span>
            </Link>
          </div>
        </nav>
        <div className="category-bar">
          <button
            type="button"
            className={`category-trigger all-trigger ${!selectedCategory ? "selected" : ""}`}
            onClick={() => {
              setSelectedCategory("");
              setSelectedSubCategory("");
              if (location.pathname !== "/") navigate("/");
            }}
          >
            All
          </button>
          {categoryMenus.map((menu) => (
            <div key={menu.title} className="category-item">
              <button
                type="button"
                className={`category-trigger ${selectedCategory === menu.title ? "selected" : ""}`}
                onClick={() => {
                  setSelectedCategory(menu.title);
                  setSelectedSubCategory("");
                  navigate(`/category/${encodeURIComponent(menu.title)}`);
                }}
              >
                {menu.title}
                {menu.items?.length > 0 ? <span className="caret"> ▼</span> : null}
              </button>
              {menu.items?.length > 0 ? (
                <div className="subcategory-menu">
                  {menu.items.map((item) => (
                    <button
                      type="button"
                      key={item}
                      className={`subcategory-item ${selectedSubCategory === item ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedCategory(menu.title);
                        setSelectedSubCategory(item);
                        navigate(`/category/${encodeURIComponent(menu.title)}?sub=${encodeURIComponent(item)}`);
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      ) : null}
      {showAuthPopup && !isLoggedIn && (
        <div className="auth-popup-overlay" role="dialog" aria-modal="true">
          <div className="auth-popup-modal">
            <AuthPage initialMode="login" />
          </div>
        </div>
      )}
      <Routes>
        <Route
          path="/"
          element={
            <Home
              selectedCategory={selectedCategory}
              selectedSubCategory={selectedSubCategory}
              searchTerm={searchTerm}
              categoryMenus={categoryMenus}
              onSelectCategory={setSelectedCategory}
              onSelectSubCategory={setSelectedSubCategory}
              onOpenCategory={onOpenCategory}
            />
          }
        />
        <Route path="/category/:categoryName" element={<CategoryWindow categoryMenus={categoryMenus} />} />
        <Route path="/info/:slug" element={<StaticInfoPage />} />
        <Route path="/product/:productId" element={<ProductDetailPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wishlist"
          element={
            <ProtectedRoute>
              <WishlistPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <MyOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/success/:orderId"
          element={
            <ProtectedRoute>
              <OrderSuccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
      </Routes>
      {!isAdminRoute ? (
      <footer className="site-footer">
        <div className="footer-grid">
          <div>
            <h4>QUICK LINKS</h4>
            <Link to="/info/shipping-policy">Shipping Policy</Link>
            <Link to="/info/terms-and-conditions">Terms & Conditions</Link>
            <Link to="/info/cancellation-refund-policy">Cancellation/Refund Policy</Link>
            <Link to="/info/about-us">About Us</Link>
            <Link to="/info/contact-us">Contact Us</Link>
            <Link to="/info/blogs">Blogs</Link>
          </div>
          <div>
            <h4>CONTACT US</h4>
            <p>Killianwali road, Mohan nagar-Z203638, Abohar, Punjab 152116, India</p>
            <p>+918847522570 , +919876573021</p>
            <p>Corrucraftindustries@gmail.com</p>
          </div>
          <div>
            <h4>PAYMENT METHODS</h4>
            <div className="payment-row">
              <span className="pay-chip">VISA</span>
              <span className="pay-chip">MasterCard</span>
              <span className="pay-chip">RuPay</span>
            </div>
            {/* <h4>KEEP IN TOUCH</h4>
            <div className="social-row">
              <span>◎</span>
              <span>f</span>
              <span>▶</span>
              <span>𝕏</span>
              <span>p</span>
            </div> */}
          </div>
        </div>
        <div className="footer-copy">© 2025-26 | All rights reserved</div>
      </footer>
      ) : null}
    </div>
  );
};

export default App;
